import phoebeAvatarSrc from "../../../public/phoebe-chat-avatar.png";
import errorFilledIconSrc from "../../assets/ErrorFilled.svg";
import loaderIconSrc from "../../assets/Loader.svg";
import liveAgentAvatarSrc from "../../assets/LiveAgentAvatar.png";
import wbIncandescentOutlinedSrc from "../../assets/WbIncandescentOutlined.svg";

import { UserMessageAttachments } from "@/components/assistant-ui/attachment";
import { formatMessageDateLabel } from "@/components/assistant-ui/date-label";
import { MarkdownText } from "@/components/assistant-ui/markdown-text";
import { hasVisibleMessageContent } from "@/components/assistant-ui/message-content";
import { SuperPhoebeMessageParts } from "@/components/assistant-ui/super-phoebe-primitives";
import { ToolFallback } from "@/components/assistant-ui/tool-fallback";
import { TooltipIconButton } from "@/components/assistant-ui/tooltip-icon-button";
import { ChatHeader } from "@/components/ChatHeader";
import {
  CloseOutlinedIcon,
  OpenInNewOutlinedIcon,
  SendOutlinedIcon,
  StopCircleOutlinedIcon,
  ThumbDownFilledIcon,
  ThumbDownOutlinedIcon,
  ThumbUpFilledIcon,
  ThumbUpOutlinedIcon,
} from "@/components/icons/chat-header-icons";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { trackUOPX } from "@/analytics";
import { UOPX_EVENT, UOPX_LOCATION, UOPX_TEXT, UOPX_DATA } from "@/analytics/events";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import {
  LEGAL_NOTICE_REMINDER_STORAGE_KEY,
  ensureLegalNoticeThreadStarted,
  readLegalNoticeReminderState,
  removeLegalNoticeReminder,
  shouldShowLegalNoticeReminder,
  writeLegalNoticeReminderState,
  type LegalNoticeReminderState,
} from "@/runtime/legalNoticeReminder";
import { useRuntimeConfig } from "@/runtime/RuntimeConfigContext";
import {
  getLiveStudentDeliveryMessage,
  getLiveStudentDeliveryStatus,
  getSocketStageMessage,
  useSocketStatusStore,
  type LiveDeliveryStatus,
} from "@/runtime/socket/socketStatusStore";
import { sendWidgetTelemetry } from "@/runtime/socket/telemetry";
import type { FollowUpSuggestion, WidgetFeedbackRating } from "@/runtime/socket/types";
import {
  AuiIf,
  BranchPickerPrimitive,
  ComposerPrimitive,
  ErrorPrimitive,
  MessagePrimitive,
  ThreadPrimitive,
  useAui,
  useComposer,
  useMessage,
  useThread,
  useThreadViewport,
} from "@assistant-ui/react";
import {
  AlertCircleIcon,
  AngryIcon,
  ArrowDownIcon,
  CalendarDaysIcon,
  CheckIcon,
  CheckCheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  FrownIcon,
  LaughIcon,
  MehIcon,
  SmileIcon,
} from "lucide-react";
import {
  createContext,
  type ButtonHTMLAttributes,
  type CSSProperties,
  type FC,
  type FormEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type Ref,
  forwardRef,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";

const THREAD_MAX_WIDTH = "376px";
const THREAD_MAX_WIDTH_VAR = "--thread-max-width";
const PHOEBE_AVATAR_SRC = phoebeAvatarSrc as unknown as string;
const ERROR_FILLED_ICON_SRC = errorFilledIconSrc as unknown as string;
const LOADER_ICON_SRC = loaderIconSrc as unknown as string;
const WB_INCANDESCENT_OUTLINED_SRC = wbIncandescentOutlinedSrc as unknown as string;
const PRIVACY_POLICY_URL = "https://www.phoenix.edu/copyright-legal/privacy-policy.html";
const TERMS_URL = "https://www.phoenix.edu/copyright-legal/terms-conditions.html";
const LEGAL_NOTICE_TEXT_CLASS =
  "font-['Roboto',sans-serif] text-[12px] leading-[16px] tracking-[0px] text-[#5e7079]";
const LEGAL_NOTICE_LINK_CLASS =
  "inline-flex items-center gap-[2px] align-baseline font-['Roboto',sans-serif] text-[12px] leading-[16px] tracking-[0px] text-[#30718d] underline decoration-from-font underline-offset-auto transition-colors hover:text-[#001823] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#001823] focus-visible:ring-offset-2";
const LEGAL_NOTICE_ICON_CLASS = "ml-[1px] size-[12px] shrink-0";
const CHAT_HOME_LEGAL_NOTICE_CLASS =
  "flex w-full flex-col items-center gap-[8px] self-stretch border-t border-[rgba(0,24,35,0.12)] px-[16px] py-[16px] text-center";
const DISMISSIBLE_LEGAL_NOTICE_CLASS =
  "mx-auto box-border flex min-h-[80px] w-full max-w-[424px] items-center gap-[8px] rounded-[16px] border border-[rgba(0,24,35,0.12)] py-[16px] pl-[16px] pr-[8px] text-center";
const THREAD_LEGAL_NOTICE_CLASS =
  "mx-auto box-border flex min-h-[80px] w-[calc(100%-32px)] max-w-[424px] items-center gap-[8px] rounded-[16px] border border-[rgba(0,24,35,0.12)] bg-white py-[16px] pl-[16px] pr-[8px] text-center shadow-sm sm:w-(--thread-max-width)";
const DISMISSIBLE_LEGAL_NOTICE_CLOSE_CLASS =
  "aui-legal-notice-close flex size-[24px] shrink-0 items-center justify-center rounded-[100px] p-[5px] text-[#5e7079] transition-colors hover:bg-[rgba(0,24,35,0.12)] hover:text-[#001823] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#001823] focus-visible:ring-offset-2";
const THREAD_WELCOME_ROOT_CLASS =
  "relative flex h-full min-h-0 w-full grow flex-col overflow-x-hidden overflow-y-auto";
const THREAD_WELCOME_CENTER_CLASS =
  "mx-auto flex w-full max-w-[327px] flex-1 flex-col items-center justify-center gap-10 p-6 sm:max-w-[360px]";
const THREAD_WELCOME_NOTICE_CLASS = "mt-auto w-full shrink-0 px-2 pb-2 sm:px-0 sm:pb-0";
const LEGAL_NOTICE_BOX_STYLE = { boxSizing: "border-box" } satisfies CSSProperties;
const THREAD_FOOTER_NOTICE_CLASS = "aui-thread-legal-notice-footer w-full shrink-0 px-[7px] pb-[8px] empty:hidden";
const USER_MESSAGE_LABEL_STYLE: CSSProperties = {
  color: "#5e7079",
  fontFamily: "Roboto, sans-serif",
  fontSize: "12px",
  fontWeight: 400,
  lineHeight: "16px",
  letterSpacing: "0px",
};
const CHAT_MESSAGE_BODY_TEXT_STYLE: CSSProperties = {
  color: "var(--text-primary, #001823)",
  fontFeatureSettings: "'liga' off, 'clig' off",
  fontFamily: "Roboto, sans-serif",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "20px",
  letterSpacing: "0px",
};
const USER_MESSAGE_DOT_SEPARATOR_STYLE: CSSProperties = {
  width: "4px",
  height: "4px",
  aspectRatio: "1 / 1",
  borderRadius: "50%",
  backgroundColor: "#D2D7D9",
};
const THREAD_FOOTER_CLASS =
  "aui-thread-viewport-footer sticky bottom-0 box-border flex w-full flex-col items-center gap-[12px] self-stretch overflow-visible bg-[#FFFFFF] px-[7px] py-[16px] relative";
const THREAD_SCROLL_TO_BOTTOM_STYLE: CSSProperties = {
  borderRadius: "50%",
  display: "inline-flex",
};
const THREAD_SCROLL_TO_BOTTOM_BUTTON_CLASS =
  "group aui-thread-scroll-to-bottom absolute bottom-full left-1/2 z-10 inline-flex size-[40px] -translate-x-1/2 items-center justify-center rounded-full bg-white text-[#001823] transition-colors hover:bg-[rgba(0,24,35,0.12)] focus-visible:outline-none disabled:invisible [&_svg]:size-[24px]";
const THREAD_SCROLL_TO_BOTTOM_TOOLTIP_CLASS =
  "inline-flex items-start bg-[#001823] text-[#FFFFFF]";
const THREAD_SCROLL_TO_BOTTOM_TOOLTIP_ARROW_CLASS = "bg-[#001823] fill-[#001823]";
// The ring utilities are suppressed-and-restored via aui-* rules in the IIFE
// build, but they are the ONLY focus indicator for @uopx/phoebe-chat library
// consumers (lib/styles.css does not include iife.css) — keep them here.
const COMPOSER_ACTION_BUTTON_CLASS =
  "mb-0 size-[34px] shrink-0 rounded-full p-[5px] text-[var(--composer-action-color)] transition-colors hover:bg-[var(--composer-action-hover-bg)] hover:text-[var(--composer-action-color)] focus-visible:outline-none focus-visible:bg-[var(--composer-action-hover-bg)] focus-visible:ring-2 focus-visible:ring-[#001823] focus-visible:ring-offset-2 disabled:opacity-100";
type ComposerActionStyle = CSSProperties & {
  "--composer-action-color": string;
  "--composer-action-hover-bg": string;
};
// UOPX MUI IconButton state matrix: the icon color is constant per variant;
// hover/focus add a circular tint of the same color family at 12%.
// Idle (blocked) send uses action/disabled (#001823 @ 38%) per the
// MessageComposer spec; the active send/stop are the Primary variant.
const COMPOSER_ACTION_DEFAULT_STYLE: ComposerActionStyle = {
  "--composer-action-color": "rgba(0,24,35,0.56)",
  "--composer-action-hover-bg": "rgba(0,24,35,0.12)",
};
const COMPOSER_ACTION_ACTIVE_STYLE: ComposerActionStyle = {
  "--composer-action-color": "#DB3725",
  "--composer-action-hover-bg": "rgba(219,55,37,0.12)",
};
const COMPOSER_ACTION_DISABLED_STYLE: ComposerActionStyle = {
  "--composer-action-color": "rgba(0,24,35,0.38)",
  "--composer-action-hover-bg": "transparent",
};
const COMPOSER_ROOT_CLASS = "aui-composer-root relative flex w-full flex-col items-start gap-[8px]";
const HOME_COMPOSER_ROOT_CLASS = "max-w-[360px]";
const THREAD_COMPOSER_ROOT_CLASS = "w-full max-w-[376px]";
// Focused = a 2px #001823 border per the MessageComposer spec (no offset
// ring). The second pixel is painted with an inset shadow so the border-box
// stays 1px and the layout does not shift between states.
const COMPOSER_FIELD_CLASS =
  "aui-composer-field group flex min-h-[48px] max-h-[144px] w-full items-end gap-2 overflow-hidden rounded-[24px] border border-[#a8b0b7] bg-[#ffffff] shadow-[0_4px_8px_0_rgba(0,24,35,0.08)] transition-colors hover:border-[#001823] focus-within:!border-[#001823] focus-within:shadow-[inset_0_0_0_1px_#001823,0_4px_8px_0_rgba(0,24,35,0.08)]";
const COMPOSER_FIELD_COMPACT_CLASS = "h-[48px]";
const COMPOSER_FIELD_ERROR_CLASS =
  "!border-[#b21f13] hover:!border-[#b21f13] focus-within:!border-[#b21f13] focus-within:!shadow-[inset_0_0_0_1px_#b21f13,0_4px_8px_0_rgba(0,24,35,0.08)]";
// Disabled spec: border input/outlined/disabledBorder #7B8A92, no fill of
// its own, placeholder text/disabled #B2BBBF (VIAF-375).
const COMPOSER_FIELD_DISABLED_CLASS = "pointer-events-none border-[#7B8A92] bg-white";
const COMPOSER_INPUT_CLASS =
  "aui-composer-input flex min-h-[24px] max-h-[32px] flex-[1_0_0] resize-none items-center justify-center gap-[8px] self-stretch border-0 bg-transparent px-[16px] py-[12px] font-['Roboto',sans-serif] text-[16px] font-normal not-italic leading-[24px] tracking-[0.15px] text-[#001823] outline-none placeholder:text-[#5e7079] focus-visible:outline-none focus-visible:ring-0 overflow-hidden";
const COMPOSER_INPUT_EXPANDED_CLASS = "box-border min-h-[48px] max-h-[142px] self-auto overflow-y-auto";
const COMPOSER_INPUT_DISABLED_CLASS = "text-[#B2BBBF] placeholder:text-[#B2BBBF]";
const COMPOSER_ACTIONS_CLASS = "flex items-end gap-[8px] self-stretch pb-[7px] pl-0 pr-[8px] pt-[8px]";
const COMPOSER_ERROR_CLASS =
  "aui-composer-error flex items-center gap-[4px] px-[2px] font-['Roboto',sans-serif] text-[12px] font-normal leading-[16px] tracking-[0px] text-[#b21f13]";
const COMPOSER_MULTILINE_HEIGHT_THRESHOLD = 56;
const LIVE_REPRESENTATIVE_MESSAGE_CLASS =
  "aui-assistant-message-root aui-live-representative-message-root fade-in slide-in-from-bottom-1 relative mx-auto flex w-[376px] max-w-[calc(100%-16px)] animate-in items-start gap-[12px] duration-150";
const ASSISTANT_MESSAGE_ROOT_CLASS =
  "aui-assistant-message-root fade-in slide-in-from-bottom-1 relative mx-auto flex max-w-[692px] flex-[1_0_0] animate-in items-start gap-[12px] self-stretch p-[16px] duration-150";
const USER_MESSAGE_ROOT_CLASS =
  "aui-user-message-root fade-in slide-in-from-bottom-1 mx-auto flex max-w-[692px] flex-[1_0_0] animate-in flex-col items-center gap-[24px] self-stretch p-[16px] duration-150";
const LIVE_REPRESENTATIVE_MAIN_CLASS = "flex min-w-0 flex-1 flex-col gap-[8px]";
const LIVE_REPRESENTATIVE_LABEL_CLASS =
  "flex min-w-0 flex-wrap items-center gap-[4px] font-['Roboto',sans-serif] text-[12px] leading-[16px] tracking-[0px] text-[#5e7079]";
const LIVE_REPRESENTATIVE_CONTENT_CLASS =
  "aui-assistant-message-content aui-live-representative-message-content min-w-0 font-['Roboto',sans-serif] text-[14px] font-normal leading-[20px] tracking-[0px] text-[#001823] wrap-break-word";
const LIVE_TYPING_INDICATOR_CLASS =
  "aui-live-typing-indicator mx-auto flex w-[336px] max-w-[calc(100%-32px)] items-center gap-[8px] py-1 font-['Roboto',sans-serif] text-[14px] font-normal leading-[20px] tracking-[0px] text-[#001823]";
const PHOEBE_LABEL = "Phoebe - AI assistant";
const USER_LABEL = "You";
const MAX_CHARS = 500;
const COMPOSER_INPUT_HARD_MAX_LENGTH = 4000;
type LiveAssistanceMessageMetadata = {
  agentName?: string;
  clientMessageId?: string;
  kind?: string;
  liveSessionId?: string;
};
type EnrollmentConsultationMetadata = {
  kind?: "advisor_consultation";
};
type EnrollmentConsultationStep = "offer" | "picker" | "selected";
type ConsultationContactMethod = "phone" | "video";
type ConsultationTimeOption = {
  id: string;
  dateLabel: string;
  timeLabel: string;
  confirmationDate: string;
};
const THINKING_MESSAGE_INTERVAL_MS = 8000;
const THINKING_MESSAGE_FADE_MS = 250;
const THINKING_INSIGHTS_DELAY_MS = 3000;
const THINKING_INSIGHTS_DISMISSED_STORAGE_KEY = "phoebe_thinking_insights_dismissed";
let thinkingInsightsDismissedInMemory = false;
const EMPTY_THINKING_INSIGHTS: string[] = [];
// Legal must approve every string here before the feature flag is enabled.
// Phase 1 must not fetch or generate thinking-state copy.
const LEGAL_APPROVED_THINKING_INSIGHTS: readonly string[] = [];

type FocusableElementRef<T extends HTMLElement> = { current: T | null };

export function scheduleInitialChatHeadingFocus(
  headingRef: FocusableElementRef<HTMLHeadingElement>,
  shouldFocus: () => boolean,
): () => void {
  const previouslyActiveElement = document.activeElement;
  const animationFrame = window.requestAnimationFrame(() => {
    if (!shouldFocus()) return;

    const heading = headingRef.current;
    if (!heading) return;

    const currentActiveElement = document.activeElement;
    const userMovedFocus =
      previouslyActiveElement &&
      currentActiveElement !== previouslyActiveElement &&
      currentActiveElement !== document.body;
    if (userMovedFocus) return;

    focusElementPreventScroll(heading);
  });

  return () => window.cancelAnimationFrame(animationFrame);
}

export function randomThinkingInsightIndex(length: number, currentIndex?: number): number {
  if (length <= 1) return 0;
  const normalizedCurrentIndex = currentIndex === undefined ? undefined : currentIndex % length;
  let nextIndex = Math.floor(Math.random() * (normalizedCurrentIndex === undefined ? length : length - 1));
  if (normalizedCurrentIndex !== undefined && nextIndex >= normalizedCurrentIndex) {
    nextIndex += 1;
  }
  return nextIndex;
}

function hasDismissedThinkingInsights(): boolean {
  if (thinkingInsightsDismissedInMemory) return true;
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(THINKING_INSIGHTS_DISMISSED_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

function writeThinkingInsightsDismissed(): void {
  thinkingInsightsDismissedInMemory = true;
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(THINKING_INSIGHTS_DISMISSED_STORAGE_KEY, "true");
  } catch {
    // Storage can be disabled by host policy; module memory covers this page session.
  }
}

function syncThinkingInsightsDismissedFromStorage(): boolean {
  const isDismissed = hasDismissedThinkingInsights();
  if (!isDismissed) {
    thinkingInsightsDismissedInMemory = false;
  }
  return isDismissed;
}

const HOME_SUGGESTIONS = [
  "Tell me about degree programs",
  "Tell me about tuition and financial aid",
  "Tell me about admissions steps",
  "Talk to support",
  "Schedule a consultation",
] as const;
const CONSULTATION_TIME_ZONE = "America/Phoenix";
const CONSULTATION_SAMPLE_TIMES = [
  { id: "first-1030", dayOffset: 4, timeLabel: "10:30 AM Arizona time" },
  { id: "first-1400", dayOffset: 4, timeLabel: "2:00 PM Arizona time" },
  { id: "second-0915", dayOffset: 5, timeLabel: "9:15 AM Arizona time" },
] as const;
const POST_ANSWER_SUGGESTIONS_CLASS =
  "aui-follow-up-suggestions mt-2 ml-1 flex w-full max-w-[320px] flex-wrap gap-[8px]";
// UOPX MUI Chip (outlined/default): 1px #B2BBBF border in every state, no
// shadow; hover/focus add a black 12% fill (VIAF-377).
const POST_ANSWER_SUGGESTION_BUTTON_CLASS =
  "aui-suggestion-chip flex h-auto min-h-[32px] min-w-0 max-w-full items-center whitespace-normal rounded-full border border-[#B2BBBF] bg-white px-[10px] py-[4px] text-left font-['Roboto'] text-[13px] font-normal leading-[18px] tracking-[0.16px] text-[#001823] transition-colors hover:bg-[rgba(0,0,0,0.12)] focus-visible:outline-none focus-visible:bg-[rgba(0,0,0,0.12)] focus-visible:ring-2 focus-visible:ring-[#001823] focus-visible:ring-offset-2";
const SAFETY_EXIT_PROMPT_CLASS = "mt-[12px] flex w-[256px] max-w-full flex-col items-stretch gap-[8px]";
// UOPX MUI outlined/secondary button: hover darkens border+label to
// secondary/dark #1A3D4D on a white fill (VIAF-431).
const SAFETY_EXIT_BUTTON_CLASS =
  "aui-safety-exit flex h-auto min-w-[64px] flex-col items-center justify-center self-stretch rounded-[17px] border-[2px] border-[#30718d] bg-white px-[12px] py-[8px] font-['Roboto',sans-serif] text-[14px] font-medium leading-[16px] tracking-[0px] text-[#30718d] shadow-none transition-colors hover:border-[#1A3D4D] hover:bg-white hover:text-[#1A3D4D] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#001823] focus-visible:ring-offset-2";
const SAFETY_EXIT_DESCRIPTION_CLASS =
  "m-0 font-['Roboto',sans-serif] text-[12px] font-normal leading-[16px] tracking-[0px] text-[#5e7079]";
// UOPX MUI IconButton (default): circular button, icon constant at
// action/active #001823 56%, hover/focus tint action/hover #001823 12%
// (VIAF-397). The thumbs sit 4px apart per the ActionBar spec.
const RATING_BUTTON_CLASS =
  "aui-rating-button inline-flex h-[24px] w-[24px] items-center justify-center rounded-full p-0 text-[rgba(0,24,35,0.56)] hover:bg-[rgba(0,24,35,0.12)] focus-visible:outline-none focus-visible:bg-[rgba(0,24,35,0.12)] focus-visible:ring-2 focus-visible:ring-[#001823] focus-visible:ring-offset-2 data-[submitted=true]:text-[rgba(0,24,35,0.56)]";
const RATING_CONTROLS_CLASS = "flex flex-wrap content-center items-center gap-[4px] self-stretch";
const RATING_ICON_FRAME_CLASS = "flex items-center justify-center";
const RATING_ICON_CLASS = "h-[16.667px] w-[18.333px] shrink-0";
const NEW_CHAT_DIALOG_CONTENT_CLASS =
  "flex max-h-[calc(100%-32px)] w-[336px] max-w-[calc(100%-32px)] flex-col gap-0 overflow-hidden rounded-[12px] border-0 bg-white p-0 shadow-lg";
const NEW_CHAT_DIALOG_HEADER_CLASS =
  "flex shrink-0 items-center justify-end self-stretch bg-white p-[8px]";
const NEW_CHAT_DIALOG_CLOSE_CLASS =
  "aui-dialog-close flex size-[24px] items-center justify-center rounded-full border-2 border-transparent text-[#001823] transition-all hover:bg-[#001823]/[0.12] focus-visible:outline-none focus-visible:bg-[#001823]/[0.12] focus-visible:ring-2 focus-visible:ring-[#001823] focus-visible:ring-offset-2";
const NEW_CHAT_DIALOG_BODY_CLASS =
  "flex min-h-0 w-full flex-col items-start gap-[16px] overflow-y-auto px-[24px] pb-[24px] pt-0";
const NEW_CHAT_DIALOG_TITLE_CLASS =
  "w-[288px] max-w-full text-center font-['Roboto',sans-serif] text-[16px] font-bold leading-[24px] tracking-[0.15px] text-[#001823]";
const NEW_CHAT_DIALOG_DESCRIPTION_CLASS =
  "w-[288px] max-w-full text-center font-['Roboto',sans-serif] text-[16px] font-normal leading-[24px] tracking-[0.15px] text-[#001823]";
const NEW_CHAT_DIALOG_ACTION_CLASS =
  "aui-dialog-primary-action aui-start-new-chat-confirm relative h-[40px] w-[288px] max-w-full min-w-[120px] overflow-visible rounded-[20px] border-[2px] border-transparent bg-[#DB3725] px-[16px] py-[8px] font-['Roboto',sans-serif] text-[16px] font-bold leading-[24px] tracking-[0px] text-white transition-[background-color,border-color,border-radius] hover:bg-[#8A1500] focus-visible:bg-[#8A1500] focus-visible:outline-none focus-visible:ring-0";
const NEW_CHAT_DIALOG_TEXT_STYLE: CSSProperties = {
  fontFeatureSettings: "'liga' off, 'clig' off",
};
const NEW_CHAT_DIALOG_OPEN_GUARD_MS = 250;
const EMPTY_FOLLOW_UP_SUGGESTIONS: FollowUpSuggestion[] = [];
const EMPTY_ASYNC_SUGGESTIONS: Map<string, FollowUpSuggestion[]> = new Map();
type FeedbackRatingValue = "hate" | "dislike" | "neutral" | "like" | "love";
const FEEDBACK_RATING_OPTIONS: Array<{
  value: FeedbackRatingValue;
  label: string;
  Icon: typeof AngryIcon;
}> = [
  { value: "hate", label: "Hate", Icon: AngryIcon },
  { value: "dislike", label: "Dislike", Icon: FrownIcon },
  { value: "neutral", label: "Neutral", Icon: MehIcon },
  { value: "like", label: "Like", Icon: SmileIcon },
  { value: "love", label: "Love", Icon: LaughIcon },
];
const FIRST_FEEDBACK_RATING_VALUE = FEEDBACK_RATING_OPTIONS[0].value;
const FEEDBACK_SURVEY_BODY_CLASS =
  "flex min-h-0 flex-col items-start gap-[16px] self-stretch overflow-y-auto px-[24px] pb-[24px] pt-0";
const FEEDBACK_DIALOG_CONTENT_CLASS =
  "flex max-h-[calc(100%-32px)] w-[360px] max-w-[calc(100%-32px)] flex-col gap-0 overflow-hidden rounded-[12px] border-0 bg-white p-0 shadow-lg";
const FEEDBACK_DIALOG_CLOSE_HEADER_CLASS = "flex shrink-0 items-center justify-end self-stretch p-[8px]";
const FEEDBACK_SUBMITTED_BODY_CLASS =
  "flex min-h-0 flex-col items-start gap-[16px] self-stretch overflow-y-auto px-[24px] py-[24px]";
const FEEDBACK_DIALOG_HEADER_GROUP_CLASS = "flex flex-col items-center gap-[8px] self-stretch";
const FEEDBACK_TITLE_CLASS =
  "w-full self-stretch text-center font-['Roboto',sans-serif] text-[16px] font-bold leading-[24px] tracking-[0.15px] text-[#001823] [font-feature-settings:'liga'_off,'clig'_off]";
const FEEDBACK_CAPTION_CLASS =
  "w-full self-stretch text-center font-['Roboto',sans-serif] text-[12px] font-normal leading-[16px] tracking-[0px] text-[#5E7079] [font-feature-settings:'liga'_off,'clig'_off]";
const FEEDBACK_PROMPT_CLASS =
  "w-full self-stretch text-center font-['Roboto',sans-serif] text-[14px] font-normal leading-[20px] tracking-[0px] text-[#001823] [font-feature-settings:'liga'_off,'clig'_off]";
const FEEDBACK_RATING_PROMPT_CLASS = `${FEEDBACK_PROMPT_CLASS} mb-[12px] block p-0`;
const FEEDBACK_SUBMIT_BUTTON_CLASS =
  "aui-dialog-primary-action aui-feedback-submit relative flex w-full min-w-[120px] flex-col items-center justify-center overflow-visible rounded-[20px] border-2 border-transparent bg-[#DB3725] px-[16px] py-[8px] font-['Roboto',sans-serif] text-[16px] font-bold leading-[24px] tracking-[0px] text-white transition-[background-color,border-color,border-radius,box-shadow] hover:bg-[#8A1500] focus:outline-none focus-visible:!border-transparent focus-visible:!bg-[#8A1500] focus-visible:!ring-0 focus-visible:!ring-offset-0 focus-visible:!shadow-none focus-visible:outline-none";
const FEEDBACK_BACKDROP_CLASS = "aui-feedback-dialog-backdrop";
const FEEDBACK_BACKDROP_STYLE = {
  position: "absolute",
  background: "rgba(0, 24, 35, 0.60)",
  backdropFilter: "blur(4px)",
  WebkitBackdropFilter: "blur(4px)",
} satisfies CSSProperties;
const FEEDBACK_CLOSE_RESET_DELAY_MS = 220;
const NEW_CHAT_HEADER_FOCUS_RETRY_DELAY_MS = 50;
const NEW_CHAT_HEADER_FOCUS_MAX_ATTEMPTS = 3;

function focusRadiogroupOption(event: ReactKeyboardEvent<HTMLButtonElement>, index: number) {
  const radioGroup = event.currentTarget.closest('[role="radiogroup"]');
  window.requestAnimationFrame(() => {
    radioGroup?.querySelectorAll<HTMLElement>('[role="radio"]')[index]?.focus();
  });
}

export function focusElementPreventScroll(element: HTMLElement | null): void {
  if (!element) return;
  try {
    element.focus({ preventScroll: true });
  } catch {
    element.focus();
  }
}

function isFocusGuardOrBody(element: Element | null): boolean {
  return element === document.body || element?.hasAttribute("data-radix-focus-guard") === true;
}

function isFocusGuardBodyOrChatWindow(element: Element | null, composer: HTMLElement): boolean {
  return (
    isFocusGuardOrBody(element) ||
    element === composer.closest<HTMLElement>("[data-testid='aichat_window']")
  );
}

type ThreadProps = {
  backendUrl?: string;
  canCancelActiveTurn?: boolean;
  isVisible?: boolean;
  unreadBoundaryMessageId?: string | null;
  homeLegalNoticePlacement?: "welcome" | "footer" | "none";
  showHeader?: boolean;
  headingRef?: FocusableElementRef<HTMLHeadingElement>;
  onMinimize?: () => void;
  onNewChat?: () => void | Promise<void>;
  onEndLiveSession?: () => void;
  onSafetyExit?: () => void;
  onAssistantFeedback?: (payload: AssistantFeedbackPayload) => void;
  onScheduleConsultation?: () => void;
  asyncSuggestions?: Map<string, FollowUpSuggestion[]>;
};

const ThreadTelemetryContext = createContext<{ backendUrl?: string; isVisible: boolean }>({ isVisible: true });
type AssistantFeedbackPayload = {
  rating: WidgetFeedbackRating;
  clientMessageId?: string;
  traceId?: string;
  langsmithTraceId?: string;
  langsmithRunId?: string;
};
const ThreadActionsContext = createContext<{
  canCancelActiveTurn: boolean;
  onSafetyExit?: () => void;
  onAssistantFeedback?: (payload: AssistantFeedbackPayload) => void;
}>({
  canCancelActiveTurn: true,
});
const AsyncSuggestionsContext = createContext<Map<string, FollowUpSuggestion[]>>(new Map());
const ThreadUnreadBoundaryContext = createContext<string | null>(null);

export const Thread: FC<ThreadProps> = ({
  backendUrl,
  canCancelActiveTurn = true,
  isVisible = true,
  unreadBoundaryMessageId = null,
  homeLegalNoticePlacement = "welcome",
  showHeader = true,
  headingRef,
  onMinimize,
  onNewChat,
  onEndLiveSession,
  onSafetyExit,
  onAssistantFeedback,
  onScheduleConsultation,
  asyncSuggestions,
}) => {
  const telemetryContextValue = useMemo(() => ({ backendUrl, isVisible }), [backendUrl, isVisible]);
  const actionsContextValue = useMemo(
    () => ({ canCancelActiveTurn, onSafetyExit, onAssistantFeedback }),
    [canCancelActiveTurn, onAssistantFeedback, onSafetyExit],
  );
  const suggestionsMap = useMemo(() => asyncSuggestions ?? EMPTY_ASYNC_SUGGESTIONS, [asyncSuggestions]);

  return (
    <ThreadTelemetryContext.Provider value={telemetryContextValue}>
      <ThreadActionsContext.Provider value={actionsContextValue}>
        <AsyncSuggestionsContext.Provider value={suggestionsMap}>
          <ThreadUnreadBoundaryContext.Provider value={unreadBoundaryMessageId}>
            <ThreadPrimitive.Root
              className="aui-root aui-thread-root @container flex h-full w-full flex-col bg-[#FFFFFF]"
              style={{ [THREAD_MAX_WIDTH_VAR]: THREAD_MAX_WIDTH } as CSSProperties & Record<string, string>}
            >
              <ThreadContent
                isVisible={isVisible}
                homeLegalNoticePlacement={homeLegalNoticePlacement}
                showHeader={showHeader}
                headingRef={headingRef}
                onMinimize={onMinimize}
                onNewChat={onNewChat}
                onEndLiveSession={onEndLiveSession}
                onScheduleConsultation={onScheduleConsultation}
              />
            </ThreadPrimitive.Root>
          </ThreadUnreadBoundaryContext.Provider>
        </AsyncSuggestionsContext.Provider>
      </ThreadActionsContext.Provider>
    </ThreadTelemetryContext.Provider>
  );
};

const ThreadContent: FC<ThreadProps> = ({
  isVisible = true,
  homeLegalNoticePlacement = "welcome",
  showHeader = true,
  headingRef: externalHeadingRef,
  onMinimize,
  onNewChat,
  onEndLiveSession,
  onScheduleConsultation,
}) => {
  const [isEndLiveChatConfirmOpen, setIsEndLiveChatConfirmOpen] = useState(false);
  const localChatHeadingRef = useRef<HTMLHeadingElement | null>(null);
  const chatHeadingRef = externalHeadingRef ?? localChatHeadingRef;

  const hasActiveLiveAssistance = useSocketStatusStore((state) => {
    const sessionState = state.liveAssistance.session?.state;
    return Boolean(
      state.liveAssistance.isConnecting ||
        state.liveAssistance.pendingHandoffClientMessageId ||
        ["connecting", "waiting_for_specialist", "live", "ending"].includes(sessionState ?? ""),
    );
  });
  const liveSession = useSocketStatusStore((state) => state.liveAssistance.session);
  const isLiveChatActive = liveSession?.state === "live";
  const isThreadEmpty = useThread((state) => state.messages.length === 0);
  const isThreadRunning = useThread((state) => state.isRunning);
  const threadTestId = isThreadEmpty ? undefined : "aichat_window_thread";
  const [shouldFocusReplacementComposer, setShouldFocusReplacementComposer] = useState(false);
  const replacementComposerInputRef = useRef<HTMLTextAreaElement | null>(null);
  const shouldRestoreReplacementComposerFocusRef = useRef(false);
  const homeSubmissionGenerationRef = useRef(0);
  const wasThreadRunningRef = useRef(isThreadRunning);

  const handleHomeComposerSubmit = useCallback(() => {
    homeSubmissionGenerationRef.current += 1;
    shouldRestoreReplacementComposerFocusRef.current = true;
    setShouldFocusReplacementComposer(true);
  }, []);

  const handleNewChat = useCallback(async () => {
    // A New Chat supersedes any pending Home-composer restore. Without this,
    // a response that finishes after the reset could consume stale intent and
    // move focus during the new conversation lifecycle.
    shouldRestoreReplacementComposerFocusRef.current = false;
    setShouldFocusReplacementComposer(false);
    await onNewChat?.();
  }, [onNewChat]);

  const handleEndLiveChat = () => {
    onEndLiveSession?.();
    setIsEndLiveChatConfirmOpen(false);
  };

  useEffect(() => {
    if (!isVisible || (!showHeader && !externalHeadingRef)) return;
    const submissionGeneration = homeSubmissionGenerationRef.current;
    return scheduleInitialChatHeadingFocus(
      chatHeadingRef,
      // Submission owns the next focus handoff. A delayed initial frame can
      // otherwise focus the heading while the Home textarea is being removed.
      () => isVisible && (showHeader || Boolean(externalHeadingRef)) &&
        homeSubmissionGenerationRef.current === submissionGeneration,
    );
  }, [chatHeadingRef, externalHeadingRef, isVisible, showHeader]);

  useEffect(() => {
    if (!shouldFocusReplacementComposer || isThreadEmpty) return;
    if (!isVisible) {
      shouldRestoreReplacementComposerFocusRef.current = false;
      setShouldFocusReplacementComposer(false);
      return;
    }
    let focusAttemptTimeout: number | null = null;
    const scheduleFocusAttempt = (attempt: number) => {
      focusAttemptTimeout = window.setTimeout(() => focusReplacementComposer(attempt), attempt === 0 ? 0 : 50);
    };
    const focusReplacementComposer = (attempt: number) => {
      const composer = replacementComposerInputRef.current;
      if (!composer?.isConnected || composer.disabled) {
        if (attempt < 3) {
          scheduleFocusAttempt(attempt + 1);
          return;
        }
        shouldRestoreReplacementComposerFocusRef.current = false;
        setShouldFocusReplacementComposer(false);
        return;
      }

      if (attempt > 0) {
        const activeElement = document.activeElement;
        const focusWasLostToGuard = isFocusGuardBodyOrChatWindow(activeElement, composer);
        if (activeElement !== composer && !focusWasLostToGuard) {
          shouldRestoreReplacementComposerFocusRef.current = false;
          setShouldFocusReplacementComposer(false);
          return;
        }
      }

      focusElementPreventScroll(composer);

      // WebKit completes the native submit/focus-guard handoff after the
      // replacement textarea mounts. Retry only when that handoff leaves
      // focus on a Radix guard or the document body; never override a user
      // who has already moved to another visible chat control.
      focusAttemptTimeout = window.setTimeout(() => {
        const activeElementAfterFocus = document.activeElement;
        const focusWasLostToGuardAfterFocus = isFocusGuardBodyOrChatWindow(activeElementAfterFocus, composer);
        if (attempt < 3 && focusWasLostToGuardAfterFocus) {
          scheduleFocusAttempt(attempt + 1);
          return;
        }
        setShouldFocusReplacementComposer(false);
      }, 50);
    };
    scheduleFocusAttempt(0);
    return () => {
      if (focusAttemptTimeout !== null) window.clearTimeout(focusAttemptTimeout);
    };
  }, [isThreadEmpty, isVisible, shouldFocusReplacementComposer]);

  useEffect(() => {
    const didFinishThread = wasThreadRunningRef.current && !isThreadRunning;
    wasThreadRunningRef.current = isThreadRunning;
    if (!didFinishThread || !shouldRestoreReplacementComposerFocusRef.current || !isVisible) return;

    let firstFrame: number | null = null;
    let secondFrame: number | null = null;
    firstFrame = window.requestAnimationFrame(() => {
      secondFrame = window.requestAnimationFrame(() => {
        const composer = replacementComposerInputRef.current;
        const activeElement = document.activeElement;
        const focusWasLost = composer
          ? isFocusGuardBodyOrChatWindow(activeElement, composer)
          : false;
        shouldRestoreReplacementComposerFocusRef.current = false;

        if (composer?.isConnected && !composer.disabled && focusWasLost) {
          focusElementPreventScroll(composer);
        }
      });
    });

    return () => {
      if (firstFrame !== null) window.cancelAnimationFrame(firstFrame);
      if (secondFrame !== null) window.cancelAnimationFrame(secondFrame);
    };
  }, [isThreadRunning, isVisible]);

  const viewportFooter = (
    <ThreadPrimitive.ViewportFooter className="aui-thread-viewport-footer sticky bottom-0 box-border flex w-full flex-col items-center gap-[12px] self-stretch overflow-visible bg-transparent px-[7px] py-[16px] relative">
      <ThreadScrollToBottom />
      <LiveTypingIndicator />
      {isLiveChatActive && (
         <div className="mx-auto flex w-full max-w-[376px] items-center gap-2 rounded-2xl border border-[rgba(0,24,35,0.12)] bg-white py-3 pl-4 pr-2 font-['Roboto',sans-serif] text-[12px] leading-[16px] font-normal tracking-[0px] text-[#5e7079]">
          <div className="flex flex-1 items-center">
            <span className="text-[#001823]">
              Connected to {liveSession?.agent?.display_name || "Support Specialist"}
            </span>
          </div>
          {onEndLiveSession && (
            <Button
              onClick={() => {
                setIsEndLiveChatConfirmOpen(true);
              }}
              variant="ghost"
              className="h-auto shrink-0 p-0 font-['Roboto',sans-serif] text-[12px] font-medium leading-[16px] text-[#30718D] transition-colors hover:bg-transparent hover:text-[#001823] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#30718D] focus-visible:ring-offset-2"
              aria-label="End live chat"
            >
              Disconnect
            </Button>
          )}
        </div>
      )}
    </ThreadPrimitive.ViewportFooter>
  );

  const widgetFooter = (
    <div className="aui-thread-composer-footer flex w-full flex-col items-center gap-[12px] bg-[#FFFFFF] px-[7px] py-[16px]">
      <Composer inputRef={replacementComposerInputRef} />
    </div>
  );

  return (
    <>
      {showHeader && (
        <ThreadHeader
          onMinimize={onMinimize}
          onNewChat={handleNewChat}
          onScheduleConsultation={onScheduleConsultation}
          headingRef={chatHeadingRef}
        />
      )}
      <ThreadPrimitive.Viewport
        className="aui-thread-viewport relative flex w-full flex-1 flex-col overflow-x-hidden overflow-y-scroll scroll-smooth bg-[#FFFFFF]"
      >
        {!hasActiveLiveAssistance && (
          <AuiIf condition={(s) => s.thread.isEmpty}>
            <ThreadWelcome
              showLegalNotice={homeLegalNoticePlacement === "welcome"}
              onComposerSubmit={handleHomeComposerSubmit}
            />
          </AuiIf>
        )}

        <div role="log" aria-live="polite" aria-label="Chat messages" data-testid={threadTestId}>
          <ThreadPrimitive.Messages
            components={{
              UserMessage,
              EditComposer,
              AssistantMessage,
            }}
          />
        </div>
        <LiveAssistanceAutoScroller />

        {hasActiveLiveAssistance ? (
          viewportFooter
        ) : (
          <AuiIf condition={(s) => !s.thread.isEmpty}>
            {viewportFooter}
          </AuiIf>
        )}
      </ThreadPrimitive.Viewport>

      {!hasActiveLiveAssistance && homeLegalNoticePlacement === "footer" && (
        <AuiIf condition={(s) => s.thread.isEmpty}>
          <ChatHomeViewLegalNotice />
        </AuiIf>
      )}

      {/* Thread-view legal notice footer — always-visible between viewport and composer when thread is active */}
      {!hasActiveLiveAssistance && (
        <AuiIf condition={(s) => !s.thread.isEmpty}>
          <ThreadFooterLegalNotice />
        </AuiIf>
      )}

      {/* Widget footer with composer - show in active thread/live states, but not on the empty home page. */}
      {hasActiveLiveAssistance ? (
        widgetFooter
      ) : (
        <AuiIf condition={(s) => !s.thread.isEmpty}>
          {widgetFooter}
        </AuiIf>
      )}

      <Dialog open={isEndLiveChatConfirmOpen} onOpenChange={setIsEndLiveChatConfirmOpen} modal={false}>
        <DialogContent
          aria-modal={false}
          className="w-[288px] gap-0 p-0 rounded-[16px] bg-white"
          showCloseButton={false}
          data-testid="aichat_window_confirm-live-chat-end"
          onEscapeKeyDown={(event) => {
            event.stopPropagation();
            setIsEndLiveChatConfirmOpen(false);
          }}
        >
          <DialogTitle className="sr-only">End live chat confirmation</DialogTitle>
          <DialogDescription className="sr-only">
            The live chat will end, but you can keep chatting with Phoebe, your AI Assistant
          </DialogDescription>
          {/* Header - close button only */}
          <div className="flex items-center justify-end gap-[10px] self-stretch p-[8px]">
            <button
              onClick={() => setIsEndLiveChatConfirmOpen(false)}
              className="flex h-[24px] w-[24px] items-center justify-center text-[#5E7079] hover:text-[#001823] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#001823] focus-visible:ring-offset-2"
              aria-label="Close dialog"
            >
              <CloseOutlinedIcon className="h-[20px] w-[20px]" />
            </button>
          </div>
          {/* Body container */}
          <div className="flex flex-col items-start gap-[16px] self-stretch px-[24px] pb-[24px] pt-0">
            <h2 className="w-full text-center font-['Roboto',sans-serif] text-[16px] font-bold leading-[24px] tracking-[0.15px] text-[#001823]" style={{ fontFeatureSettings: "'liga' off, 'clig' off" }}>
              Do you want to end the live chat?
            </h2>
            <p className="w-full text-center font-['Roboto',sans-serif] text-[14px] font-normal leading-[20px] text-[#5E7079]">
              The live chat will end, but you can keep chatting with Phoebe, your AI Assistant.
            </p>
            <Button
              onClick={handleEndLiveChat}
              className="h-[40px] w-full rounded-[20px] bg-[#D32F2F] font-['Roboto',sans-serif] text-[14px] font-medium leading-[16px] tracking-[1.25px] text-white hover:bg-[#B71C1C] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#001823] focus-visible:ring-offset-2"
            >
              End live chat
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const ThreadHeader: FC<{
  dismissAction?: "close" | "minimize";
  headingRef?: FocusableElementRef<HTMLHeadingElement>;
  onMinimize?: () => void;
  onNewChat?: () => void | Promise<void>;
  onScheduleConsultation?: () => void;
}> = ({ dismissAction, headingRef, onMinimize, onNewChat, onScheduleConsultation }) => {
  const { enrollmentConsultationEnabled } = useRuntimeConfig();
  const { backendUrl } = useContext(ThreadTelemetryContext);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [feedbackDialogVersion, setFeedbackDialogVersion] = useState(0);
  const optionsButtonRef = useRef<HTMLButtonElement | null>(null);
  const optionsFocusFrameRef = useRef<number | null>(null);
  const feedbackOpenTimeoutRef = useRef<number | null>(null);
  const lastFeedbackCloseAtRef = useRef<number | null>(null);
  const feedbackOpenedAtRef = useRef<number | null>(null);
  const confirmOpenedAtRef = useRef<number | null>(null);
  const explicitConfirmCloseRef = useRef(false);
  const preventNewChatFocusRestoreRef = useRef(false);
  const newChatHeaderFocusRetryRef = useRef<number | null>(null);

  const cancelOptionsFocusFrame = useCallback(() => {
    if (optionsFocusFrameRef.current === null) return;
    window.cancelAnimationFrame(optionsFocusFrameRef.current);
    optionsFocusFrameRef.current = null;
  }, []);

  const cancelFeedbackOpenTimeout = useCallback(() => {
    if (feedbackOpenTimeoutRef.current === null) return;
    window.clearTimeout(feedbackOpenTimeoutRef.current);
    feedbackOpenTimeoutRef.current = null;
  }, []);

  const cancelNewChatHeaderFocusRetry = useCallback(() => {
    if (newChatHeaderFocusRetryRef.current === null) return;
    window.clearTimeout(newChatHeaderFocusRetryRef.current);
    newChatHeaderFocusRetryRef.current = null;
  }, []);

  const openConfirmDialog = useCallback(() => {
    cancelOptionsFocusFrame();
    cancelFeedbackOpenTimeout();
    setIsFeedbackOpen(false);
    confirmOpenedAtRef.current = window.performance.now();
    explicitConfirmCloseRef.current = false;
    preventNewChatFocusRestoreRef.current = false;
    setIsConfirmOpen(true);
  }, [cancelFeedbackOpenTimeout, cancelOptionsFocusFrame]);

  const closeConfirmDialog = useCallback(() => {
    explicitConfirmCloseRef.current = true;
    preventNewChatFocusRestoreRef.current = false;
    // Track popup close
    trackUOPX(UOPX_EVENT.CLOSE, UOPX_TEXT.START_NEW_CHAT, UOPX_LOCATION.POPUP);
    setIsConfirmOpen(false);
    cancelOptionsFocusFrame();
    optionsFocusFrameRef.current = window.requestAnimationFrame(() => {
      optionsFocusFrameRef.current = null;
      optionsButtonRef.current?.focus();
    });
  }, [cancelOptionsFocusFrame]);

  const handleConfirmOpenChange = useCallback((nextOpen: boolean) => {
    if (nextOpen) {
      openConfirmDialog();
      return;
    }
    const openedAt = confirmOpenedAtRef.current;
    const isInitialDismiss =
      openedAt !== null &&
      !explicitConfirmCloseRef.current &&
      window.performance.now() - openedAt < NEW_CHAT_DIALOG_OPEN_GUARD_MS;
    if (isInitialDismiss) return;
    closeConfirmDialog();
  }, [closeConfirmDialog, openConfirmDialog]);

  const openFeedbackDialog = useCallback(() => {
    cancelOptionsFocusFrame();
    cancelFeedbackOpenTimeout();
    setIsConfirmOpen(false);
    const now = window.performance.now();
    const elapsedSinceClose = lastFeedbackCloseAtRef.current === null
      ? FEEDBACK_CLOSE_RESET_DELAY_MS
      : now - lastFeedbackCloseAtRef.current;
    const openDelay = Math.max(0, FEEDBACK_CLOSE_RESET_DELAY_MS - elapsedSinceClose);
    feedbackOpenTimeoutRef.current = window.setTimeout(() => {
      feedbackOpenTimeoutRef.current = null;
      lastFeedbackCloseAtRef.current = null;
      feedbackOpenedAtRef.current = window.performance.now();
      setFeedbackDialogVersion((version) => version + 1);
      setIsFeedbackOpen(true);
    }, openDelay);
  }, [cancelFeedbackOpenTimeout, cancelOptionsFocusFrame]);

  const closeFeedbackDialog = useCallback(() => {
    cancelFeedbackOpenTimeout();
    lastFeedbackCloseAtRef.current = window.performance.now();
    // Track feedback close
    trackUOPX(UOPX_EVENT.CLOSE, UOPX_TEXT.GIVE_FEEDBACK, UOPX_LOCATION.FEEDBACK);
    setIsFeedbackOpen(false);
    cancelOptionsFocusFrame();
    optionsFocusFrameRef.current = window.requestAnimationFrame(() => {
      optionsFocusFrameRef.current = null;
      optionsButtonRef.current?.focus();
    });
  }, [cancelFeedbackOpenTimeout, cancelOptionsFocusFrame]);

  const handleFeedbackOpenChange = useCallback((nextOpen: boolean) => {
    if (nextOpen) {
      openFeedbackDialog();
      return;
    }
    const openedAt = feedbackOpenedAtRef.current;
    if (openedAt !== null && window.performance.now() - openedAt < NEW_CHAT_DIALOG_OPEN_GUARD_MS) return;
    closeFeedbackDialog();
  }, [closeFeedbackDialog, openFeedbackDialog]);

  const focusNewChatHeader = useCallback(() => {
    cancelNewChatHeaderFocusRetry();
    const focusAttempt = (attempt: number) => {
      window.requestAnimationFrame(() => {
        const heading = headingRef?.current;
        if (heading?.isConnected) {
          focusElementPreventScroll(heading);
          return;
        }
        if (attempt >= NEW_CHAT_HEADER_FOCUS_MAX_ATTEMPTS) return;
        newChatHeaderFocusRetryRef.current = window.setTimeout(
          () => focusAttempt(attempt + 1),
          NEW_CHAT_HEADER_FOCUS_RETRY_DELAY_MS,
        );
      });
    };
    focusAttempt(0);
  }, [cancelNewChatHeaderFocusRetry, headingRef]);

  const handleClear = useCallback(() => {
    explicitConfirmCloseRef.current = true;
    preventNewChatFocusRestoreRef.current = true;
    // Track new chat confirmation
    trackUOPX(UOPX_EVENT.OPTION_SELECTED, UOPX_TEXT.START_NEW_CHAT, UOPX_LOCATION.POPUP);
    setIsConfirmOpen(false);
    cancelFeedbackOpenTimeout();

    // New Chat is a navigation reset. Return focus to the chat header so
    // keyboard users restart at the beginning of the widget's tab order.
    focusNewChatHeader();
    void Promise.resolve()
      .then(() => onNewChat?.())
      .catch((error: unknown) => {
        // Keep the existing chat usable if a consumer-owned reset rejects,
        // but surface the failure through the widget's existing error event.
        console.error("[phoebe-chat] New chat reset failed", error);
        if (backendUrl) sendWidgetTelemetry({ backendUrl, eventName: "widget_error" });
      });
  }, [backendUrl, cancelFeedbackOpenTimeout, focusNewChatHeader, onNewChat]);

  useEffect(() => cancelNewChatHeaderFocusRetry, [cancelNewChatHeaderFocusRetry]);

  useEffect(() => {
    if (!isConfirmOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      closeConfirmDialog();
    };

    window.addEventListener("keydown", handleKeyDown, { capture: true });
    document.addEventListener("keydown", handleKeyDown, { capture: true });
    return () => {
      window.removeEventListener("keydown", handleKeyDown, { capture: true });
      document.removeEventListener("keydown", handleKeyDown, { capture: true });
    };
  }, [closeConfirmDialog, isConfirmOpen]);

  useEffect(() => () => {
    cancelOptionsFocusFrame();
    cancelFeedbackOpenTimeout();
  }, [cancelFeedbackOpenTimeout, cancelOptionsFocusFrame]);

  return (
    <>
      <ChatHeader
        dismissAction={dismissAction}
        onGiveFeedback={openFeedbackDialog}
        headingRef={headingRef}
        onNewChat={onNewChat ? openConfirmDialog : undefined}
        optionsButtonRef={optionsButtonRef}
        onMinimize={onMinimize}
        onScheduleConsultation={enrollmentConsultationEnabled ? onScheduleConsultation : undefined}
      />
      <Dialog open={isConfirmOpen} onOpenChange={handleConfirmOpenChange} modal={false}>
        <DialogContent
          aria-modal={false}
          className={NEW_CHAT_DIALOG_CONTENT_CLASS}
          showCloseButton={false}
          data-testid="aichat_window_start-new-chat_dialog"
          // ADOD dialog pattern (VIAF-448): the dialog closes only via the
          // X button or Escape — never from clicks outside it.
          onInteractOutside={(event) => {
            event.preventDefault();
          }}
          onPointerDownOutside={(event) => {
            event.preventDefault();
          }}
          onEscapeKeyDown={(event) => {
            event.preventDefault();
            closeConfirmDialog();
          }}
          onCloseAutoFocus={(event) => {
            if (!preventNewChatFocusRestoreRef.current) return;
            event.preventDefault();
            preventNewChatFocusRestoreRef.current = false;
          }}
        >
          <header
            className={NEW_CHAT_DIALOG_HEADER_CLASS}
            data-testid="aichat_window_start-new-chat_header"
          >
            <button
              type="button"
              className={NEW_CHAT_DIALOG_CLOSE_CLASS}
              aria-label="Close"
              onClick={closeConfirmDialog}
            >
              <CloseOutlinedIcon className="size-[20px]" aria-hidden="true" />
            </button>
          </header>
          <div className={NEW_CHAT_DIALOG_BODY_CLASS} data-testid="aichat_window_start-new-chat_body">
            <DialogHeader className="contents">
              <DialogTitle className={NEW_CHAT_DIALOG_TITLE_CLASS} style={NEW_CHAT_DIALOG_TEXT_STYLE}>Do you want to start a new chat?</DialogTitle>
              <DialogDescription className={NEW_CHAT_DIALOG_DESCRIPTION_CLASS} style={NEW_CHAT_DIALOG_TEXT_STYLE}>
                This will clear your chat history.
              </DialogDescription>
            </DialogHeader>
            <Button
              onClick={handleClear}
              variant="default"
              className={NEW_CHAT_DIALOG_ACTION_CLASS}
              data-testid="aichat_window_start-new-chat_yes"
            >
              Start a new chat
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <FeedbackSurveyDialog key={feedbackDialogVersion} open={isFeedbackOpen} onOpenChange={handleFeedbackOpenChange} />
    </>
  );
};

const FeedbackSurveyDialog: FC<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
}> = ({ open, onOpenChange }) => {
  const [rating, setRating] = useState<FeedbackRatingValue | null>(null);
  const [comment, setComment] = useState("");
  const [showRatingError, setShowRatingError] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const successAnnouncementRef = useRef<HTMLDivElement | null>(null);
  const ratingPromptId = useId();
  const ratingErrorId = useId();

  const reset = useCallback(() => {
    setRating(null);
    setComment("");
    setShowRatingError(false);
    setIsSubmitted(false);
  }, []);

  useEffect(() => {
    if (!open) return;
    reset();
  }, [open, reset]);

  useEffect(() => {
    if (!isSubmitted) return;

    const focusFrame = window.requestAnimationFrame(() => {
      successAnnouncementRef.current?.focus();
    });
    return () => window.cancelAnimationFrame(focusFrame);
  }, [isSubmitted]);

  const handleOpenChange = (nextOpen: boolean) => {
    onOpenChange(nextOpen);
    if (nextOpen) {
      reset();
    }
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!rating) {
      setShowRatingError(true);
      return;
    }
    // Track feedback submission with actual rating value
    trackUOPX(UOPX_EVENT.FEEDBACK_SUBMIT, rating, UOPX_LOCATION.FEEDBACK);
    setIsSubmitted(true);
  };

  const handleDone = () => {
    handleOpenChange(false);
  };

  const selectRating = (value: FeedbackRatingValue) => {
    setRating(value);
    setShowRatingError(false);
  };

  const handleRatingKeyDown = (event: ReactKeyboardEvent<HTMLButtonElement>, value: FeedbackRatingValue) => {
    const currentIndex = FEEDBACK_RATING_OPTIONS.findIndex((option) => option.value === value);
    let nextIndex: number | undefined;

    if (event.key === "ArrowRight" || event.key === "ArrowDown") {
      nextIndex = (currentIndex + 1) % FEEDBACK_RATING_OPTIONS.length;
    }
    if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
      nextIndex = (currentIndex - 1 + FEEDBACK_RATING_OPTIONS.length) % FEEDBACK_RATING_OPTIONS.length;
    }
    if (event.key === "Home") {
      nextIndex = 0;
    }
    if (event.key === "End") {
      nextIndex = FEEDBACK_RATING_OPTIONS.length - 1;
    }

    if (nextIndex === undefined) return;

    event.preventDefault();
    const nextValue = FEEDBACK_RATING_OPTIONS[nextIndex].value;
    selectRating(nextValue);
    focusRadiogroupOption(event, nextIndex);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className={FEEDBACK_DIALOG_CONTENT_CLASS}
        data-testid="aichat_window_give-feedback"
        overlayClassName={FEEDBACK_BACKDROP_CLASS}
        overlayStyle={FEEDBACK_BACKDROP_STYLE}
        showCloseButton={false}
        onCloseAutoFocus={(event) => event.preventDefault()}
        // ADOD dialog pattern (VIAF-448): only the X button, Done, or Escape
        // dismiss the survey — outside clicks are ignored.
        onInteractOutside={(event) => {
          event.preventDefault();
        }}
        onPointerDownOutside={(event) => {
          event.preventDefault();
        }}
        onEscapeKeyDown={(event) => {
          event.preventDefault();
          event.stopPropagation();
          handleOpenChange(false);
        }}
      >
        {isSubmitted ? (
          <div className={FEEDBACK_SUBMITTED_BODY_CLASS} data-testid="aichat_window_give-feedback_body">
            <div
              ref={successAnnouncementRef}
              role="status"
              aria-live="polite"
              aria-atomic="true"
              tabIndex={-1}
            >
              <DialogTitle className={FEEDBACK_TITLE_CLASS}>Thanks for your feedback!</DialogTitle>
            </div>
            <Button
              className={FEEDBACK_SUBMIT_BUTTON_CLASS}
              data-testid="aichat_window_give-feedback_done"
              onClick={handleDone}
            >
              Done
            </Button>
          </div>
        ) : (
          <>
            <div className={FEEDBACK_DIALOG_CLOSE_HEADER_CLASS} data-testid="aichat_window_give-feedback_header">
              <DialogClose asChild>
                {/* UOPX MUI IconButton (inherit): constant icon color with a
                    circular action/hover 12% tint on hover/focus (VIAF-447). */}
                <button
                  type="button"
                  className="aui-dialog-close flex size-[24px] items-center justify-center rounded-full text-[#001823] transition-colors hover:bg-[rgba(0,24,35,0.12)] focus-visible:outline-none focus-visible:bg-[rgba(0,24,35,0.12)] focus-visible:ring-2 focus-visible:ring-[#001823] focus-visible:ring-offset-2"
                  aria-label="Close feedback survey"
                  data-testid="aichat_window_give-feedback_close"
                >
                  <CloseOutlinedIcon className="size-[20px]" />
                </button>
              </DialogClose>
            </div>
            <form
              className={FEEDBACK_SURVEY_BODY_CLASS}
              data-testid="aichat_window_give-feedback_body"
              onSubmit={handleSubmit}
            >
              <DialogHeader className={FEEDBACK_DIALOG_HEADER_GROUP_CLASS}>
                <DialogTitle className={FEEDBACK_TITLE_CLASS}>Give feedback</DialogTitle>
                <DialogDescription className={FEEDBACK_CAPTION_CLASS}>* Required information</DialogDescription>
              </DialogHeader>

              <fieldset className="flex w-full flex-col items-start gap-0 border-0 p-0">
                <legend id={ratingPromptId} className={FEEDBACK_RATING_PROMPT_CLASS}>
                  Rate your chat experience *
                </legend>
                <div
                  className="flex w-full flex-wrap content-start items-start justify-center gap-[8px] self-stretch"
                  role="radiogroup"
                  aria-labelledby={ratingPromptId}
                  aria-required="true"
                  aria-invalid={showRatingError ? "true" : undefined}
                  aria-describedby={showRatingError ? ratingErrorId : undefined}
                >
                  {FEEDBACK_RATING_OPTIONS.map(({ value, label, Icon }) => {
                    const isSelected = rating === value;
                    return (
                      <button
                        key={value}
                        type="button"
                        className={cn(
                          // RatingButton spec (node 2045-41846): hover/focus
                          // fill action/hover #001823 @ 12%; pressed border +
                          // content primary/dark #8A1500 (VIAF-450).
                          "aui-feedback-rating flex flex-col items-center gap-[4px] rounded-[4px] border border-transparent px-[10px] py-[8px] font-['Roboto',sans-serif] text-[12px] font-normal leading-[16px] tracking-[0px] text-[#001823] transition-colors hover:bg-[rgba(0,24,35,0.12)] focus-visible:outline-none focus-visible:bg-[rgba(0,24,35,0.12)] focus-visible:ring-2 focus-visible:ring-[#001823] focus-visible:ring-offset-2",
                          isSelected && "border-[#8A1500] text-[#8A1500]",
                        )}
                        role="radio"
                        aria-checked={isSelected}
                        tabIndex={(rating ?? FIRST_FEEDBACK_RATING_VALUE) === value ? 0 : -1}
                        data-testid={`aichat_window_give-feedback_rating-${value}`}
                        onClick={() => selectRating(value)}
                        onKeyDown={(event) => handleRatingKeyDown(event, value)}
                      >
                        <Icon className="size-[18px]" aria-hidden="true" />
                        <span>{label}</span>
                      </button>
                    );
                  })}
                </div>
                {showRatingError && (
                  <div
                    id={ratingErrorId}
                    className="mt-[12px] flex min-h-[32px] w-full items-center gap-[8px] rounded-[2px] border border-[#DB3725] bg-[#fff2f0] px-[8px] py-[7px] font-['Roboto',sans-serif] text-[12px] font-normal leading-[16px] tracking-[0px] text-[#b21f13]"
                    role="alert"
                  >
                    <AlertCircleIcon
                      aria-label="Error"
                      className="size-[16px] shrink-0 fill-current stroke-white stroke-[2.75]"
                      role="img"
                    />
                    <span>Choose a rating</span>
                  </div>
                )}
              </fieldset>

              <label className="flex w-full flex-col items-start gap-[12px]">
                <span className={FEEDBACK_PROMPT_CLASS}>Please tell us more</span>
                <div className="flex h-[104px] min-h-[104px] w-full flex-[1_0_0] flex-col items-start self-stretch rounded-[4px] border border-[#5E7079] bg-white px-[12px] py-[16px] focus-within:ring-2 focus-within:ring-[#001823] focus-within:ring-offset-2">
                  <textarea
                    className="h-full min-h-0 w-full flex-1 resize-none overflow-y-auto border-0 bg-transparent p-0 font-['Roboto',sans-serif] text-[16px] font-normal leading-[24px] tracking-[0.15px] text-[#001823] [font-feature-settings:'liga'_off,'clig'_off] placeholder:text-[#5E7079] focus-visible:outline-none focus-visible:ring-0"
                    placeholder="Why did you choose that rating?"
                    data-testid="aichat_window_give-feedback_comment"
                    value={comment}
                    onChange={(event) => setComment(event.target.value)}
                  />
                </div>
              </label>

              <Button
                type="submit"
                className={FEEDBACK_SUBMIT_BUTTON_CLASS}
                data-testid="aichat_window_give-feedback_submit"
              >
                Submit
              </Button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

const ThreadScrollToBottom: FC = () => {
  const isAtBottom = useThreadViewport((state) => state.isAtBottom);
  const scrollToBottom = useThreadViewport((state) => state.scrollToBottom);
  const viewportElement = useThreadViewport((state) => state.element.viewport);
  const [isScrollButtonDisabled, setIsScrollButtonDisabled] = useState(isAtBottom);

  useEffect(() => {
    if (!(viewportElement instanceof HTMLElement)) {
      return;
    }

    const updateDisabledState = () => {
      const isActuallyAtBottom =
        viewportElement.scrollHeight <= viewportElement.clientHeight + 1 ||
        Math.abs(viewportElement.scrollHeight - viewportElement.scrollTop - viewportElement.clientHeight) <= 1;
      setIsScrollButtonDisabled(isActuallyAtBottom);
    };

    updateDisabledState();
    const scrollListenerOptions: AddEventListenerOptions = { passive: true };
    viewportElement.addEventListener("scroll", updateDisabledState, scrollListenerOptions);
    const resizeObserver = new ResizeObserver(updateDisabledState);
    resizeObserver.observe(viewportElement);

    return () => {
      viewportElement.removeEventListener("scroll", updateDisabledState, scrollListenerOptions);
      resizeObserver.disconnect();
    };
  }, [viewportElement]);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label="Scroll to bottom"
          data-testid="aichat_window_scroll-to-bottom"
          disabled={isScrollButtonDisabled}
          className={THREAD_SCROLL_TO_BOTTOM_BUTTON_CLASS}
          style={THREAD_SCROLL_TO_BOTTOM_STYLE}
          onClick={() => scrollToBottom({ behavior: "smooth" })}
        >
          <ArrowDownIcon aria-hidden="true" />
          <span className="aui-sr-only sr-only">Scroll to bottom</span>
        </button>
      </TooltipTrigger>
      <TooltipContent
        side="bottom"
        className={THREAD_SCROLL_TO_BOTTOM_TOOLTIP_CLASS}
        arrowClassName={THREAD_SCROLL_TO_BOTTOM_TOOLTIP_ARROW_CLASS}
      >
        Scroll to bottom
      </TooltipContent>
    </Tooltip>
  );
};

const LiveAssistanceAutoScroller: FC = () => {
  const lastLiveMessageId = useThread((state) => {
    const message = state.messages.at(-1);
    const liveAssistance = message?.metadata.custom?.liveAssistance as { kind?: unknown } | undefined;
    const kind = typeof liveAssistance?.kind === "string" ? liveAssistance.kind : null;
    if (
      message?.role !== "assistant" ||
      !kind ||
      !["agent_message", "handoff_marker", "ended", "error", "handoff_failed", "system_message", "queue_status"].includes(kind)
    ) {
      return null;
    }
    return message.id ?? null;
  });
  const scrollToBottom = useThreadViewport((state) => state.scrollToBottom);
  const lastScrolledMessageIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!lastLiveMessageId) return;
    if (lastScrolledMessageIdRef.current === lastLiveMessageId) return;
    lastScrolledMessageIdRef.current = lastLiveMessageId;

    const frame = requestAnimationFrame(() => {
      scrollToBottom({ behavior: "smooth" });
    });
    return () => cancelAnimationFrame(frame);
  }, [lastLiveMessageId, scrollToBottom]);

  return null;
};

const MessageDateSeparator: FC = () => {
  const currentId = useMessage((message) => message.id);
  const currentCreatedAt = useMessage((message) => message.createdAt);
  const shouldShow = useThread((state) => {
    const currentIndex = state.messages.findIndex((message) => message.id === currentId);
    if (currentIndex <= 0) return currentIndex === 0;
    const previous = state.messages[currentIndex - 1];
    return !isSameCalendarDay(toMessageDate(previous?.createdAt), toMessageDate(currentCreatedAt));
  });

  if (!shouldShow) return null;

  return (
    <div className="aui-message-date-separator mx-auto flex w-[352px] max-w-[calc(100%-32px)] items-center justify-center py-2 font-['Roboto',sans-serif] text-[12px] leading-[16px] font-normal tracking-[0px] text-[#5e7079] sm:max-w-[352px]">
      {formatMessageDateLabel(toMessageDate(currentCreatedAt))}
    </div>
  );
};

const MessageBoundaryMarkers: FC = () => (
  <>
    <MessageDateSeparator />
    <MessageUnreadBoundary />
  </>
);

const MessageUnreadBoundary: FC = () => {
  const unreadBoundaryMessageId = useContext(ThreadUnreadBoundaryContext);
  const { isVisible } = useContext(ThreadTelemetryContext);
  const currentMessageId = useMessage((message) => message.id ?? null);
  const shouldShow = Boolean(unreadBoundaryMessageId && currentMessageId === unreadBoundaryMessageId);
  const labelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!shouldShow || !isVisible) return;
    let secondFrame = 0;
    const firstFrame = requestAnimationFrame(() => {
      secondFrame = requestAnimationFrame(() => {
        labelRef.current?.scrollIntoView({ block: "center", inline: "nearest", behavior: "auto" });
      });
    });
    return () => {
      cancelAnimationFrame(firstFrame);
      cancelAnimationFrame(secondFrame);
    };
  }, [isVisible, shouldShow]);

  if (!shouldShow) return null;

  return <UnreadLabel ref={labelRef} />;
};

export const UnreadLabel = forwardRef<HTMLDivElement>((_, ref) => (
  <div
    ref={ref}
    className="aui-unread-label mx-auto flex w-[352px] max-w-[calc(100%-32px)] items-center gap-[8px] py-[8px] font-['Roboto',sans-serif] text-[12px] font-normal leading-[16px] tracking-[0px] text-[#30718D] sm:max-w-[352px]"
    data-testid="aichat_window_unread-label"
    role="separator"
    aria-label="Unread messages"
  >
    <span className="h-px flex-1 bg-[rgba(0,24,35,0.12)]" aria-hidden="true" />
    <span className="w-[39px] text-center">Unread</span>
    <span className="h-px flex-1 bg-[rgba(0,24,35,0.12)]" aria-hidden="true" />
  </div>
));
UnreadLabel.displayName = "UnreadLabel";

function useMessageTime(): string {
  const createdAt = useMessage((message) => message.createdAt);
  return formatMessageTime(toMessageDate(createdAt));
}

function toMessageDate(value: unknown): Date {
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
  if (typeof value === "string" || typeof value === "number") {
    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) return date;
  }
  return new Date();
}

function isSameCalendarDay(left: Date, right: Date): boolean {
  return left.getFullYear() === right.getFullYear() &&
    left.getMonth() === right.getMonth() &&
    left.getDate() === right.getDate();
}

function formatMessageTime(date: Date): string {
  return new Intl.DateTimeFormat("en-US", { hour: "numeric", minute: "2-digit" }).format(date).toLowerCase();
}

const PhoebeAvatar: FC = () => (
  <Avatar className="h-[28px] w-[27.999px] shrink-0 self-start mt-0.5">
    <AvatarImage src={PHOEBE_AVATAR_SRC} alt="Phoebe" />
    <AvatarFallback>P</AvatarFallback>
  </Avatar>
);

const LiveAgentAvatar: FC<{ agentName: string }> = ({ agentName }) => (
  <Avatar className="size-7 shrink-0 self-start mt-0.5">
    <AvatarImage
      src={liveAgentAvatarSrc}
      alt="Live agent"
      data-testid="aichat_window_message-live-agent"
    />
    <AvatarFallback>{agentName.charAt(0).toUpperCase()}</AvatarFallback>
  </Avatar>
);

const LegalNoticeLink: FC<{ href: string; children: ReactNode }> = ({ href, children }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className={LEGAL_NOTICE_LINK_CLASS}
  >
    <span>{children}</span>
    <OpenInNewOutlinedIcon className={LEGAL_NOTICE_ICON_CLASS} />
    <span className="sr-only"> (Opens in new window)</span>
  </a>
);

const LegalNoticeBody: FC<{ align?: "left" | "center" }> = ({ align = "center" }) => (
  <p className={cn(LEGAL_NOTICE_TEXT_CLASS, "m-0", align === "left" ? "text-left" : "text-center")}>
    By continuing, you acknowledge our{" "}
    <LegalNoticeLink href={PRIVACY_POLICY_URL}>
      Privacy Policy
    </LegalNoticeLink>{" "}
    and agree to our{" "}
    <LegalNoticeLink href={TERMS_URL}>
      Terms &amp; Conditions
    </LegalNoticeLink>
    , including our dispute resolution provisions. Do not enter any sensitive information. Chat transcripts are used as described in our{" "}
    <LegalNoticeLink href={PRIVACY_POLICY_URL}>
      Privacy Policy
    </LegalNoticeLink>
    .
  </p>
);

export const ChatHomeViewLegalNotice: FC<{ framed?: boolean }> = ({ framed = true }) => (
  <div
    role="region"
    aria-label="Privacy notice"
    className={framed ? CHAT_HOME_LEGAL_NOTICE_CLASS : "flex w-full flex-col items-center gap-[8px] self-stretch text-center"}
    data-testid="aichat_window_home-legal-notice"
  >
    <LegalNoticeBody />
  </div>
);

export const DismissibleLegalNotice: FC<{ onDismiss?: () => void }> = ({ onDismiss }) => {
  const [isVisible, setIsVisible] = useState(true);
  if (!isVisible) return null;

  const handleDismiss = () => {
    setIsVisible(false);
    onDismiss?.();
  };

  return (
    <div
      role="region"
      aria-label="Privacy notice"
      className={DISMISSIBLE_LEGAL_NOTICE_CLASS}
      data-testid="aichat_window_legal-notice"
      style={LEGAL_NOTICE_BOX_STYLE}
    >
      <div className="min-w-0 flex-1">
        <LegalNoticeBody align="left" />
      </div>
      <button
        type="button"
        aria-label="Close disclaimer"
        className={DISMISSIBLE_LEGAL_NOTICE_CLOSE_CLASS}
        data-testid="aichat_window_legal-notice_close"
        onClick={handleDismiss}
      >
        <CloseOutlinedIcon className="size-[14px]" />
      </button>
    </div>
  );
};

const ThreadLegalNoticeReminder: FC<{ canDisplay?: boolean }> = ({ canDisplay = true }) => {
  const { isVisible, remove } = useThreadLegalNoticeReminder();
  if (!canDisplay || !isVisible) return null;

  return (
    <div
      role="region"
      aria-label="Privacy notice"
      className={THREAD_LEGAL_NOTICE_CLASS}
      data-testid="aichat_window_legal-notice"
      style={LEGAL_NOTICE_BOX_STYLE}
    >
      <div className="min-w-0 flex-1">
        <LegalNoticeBody align="left" />
      </div>
      <button
        type="button"
        aria-label="Close disclaimer"
        className={DISMISSIBLE_LEGAL_NOTICE_CLOSE_CLASS}
        data-testid="aichat_window_legal-notice_close"
        onClick={remove}
      >
        <CloseOutlinedIcon className="size-[14px]" />
      </button>
    </div>
  );
};

function useThreadLegalNoticeReminder(): { isVisible: boolean; remove: () => void } {
  const messages = useThread((state) => state.messages);
  const userMessageCount = countUserMessages(messages);
  const hasMessages = messages.length > 0;
  const [state, setState] = useState<LegalNoticeReminderState>(() => readLegalNoticeReminderState());
  const previousUserMessageCountRef = useRef(userMessageCount);

  const syncState = (nextState: LegalNoticeReminderState) => {
    writeLegalNoticeReminderState(nextState);
    setState(nextState);
  };

  useEffect(() => {
    if (!hasMessages || (state.threadStartedAt !== null && state.nextShowAt !== null)) return;
    const nextState = ensureLegalNoticeThreadStarted(readLegalNoticeReminderState());
    syncState(nextState);
  }, [hasMessages, state.nextShowAt, state.threadStartedAt]);

  useEffect(() => {
    const handleStorage = (event: Event) => {
      if (event instanceof StorageEvent && event.key !== LEGAL_NOTICE_REMINDER_STORAGE_KEY) return;
      setState(readLegalNoticeReminderState());
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  useEffect(() => {
    const delay = getLegalNoticeReminderDelay(state);
    if (delay === null) return;
    const timeoutId = window.setTimeout(() => setState(readLegalNoticeReminderState()), delay);
    return () => window.clearTimeout(timeoutId);
  }, [state.nextShowAt]);

  const isVisible = hasMessages && shouldShowLegalNoticeReminder(state);

  useEffect(() => {
    if (userMessageCount <= previousUserMessageCountRef.current) {
      previousUserMessageCountRef.current = userMessageCount;
      return;
    }
    previousUserMessageCountRef.current = userMessageCount;
    if (!isVisible) return;
    syncState(removeLegalNoticeReminder(readLegalNoticeReminderState()));
  }, [isVisible, userMessageCount]);

  return {
    isVisible,
    remove: () => {
      const next = removeLegalNoticeReminder(readLegalNoticeReminderState());
      syncState(next);
      // Dispatch a synthetic storage event so any other same-document hook
      // instances observe the dismissal. Use a plain Event instead of
      // StorageEvent to avoid cross-browser constructor pitfalls; the listener
      // below handles both real StorageEvents (with key) and plain Events.
      window.dispatchEvent(new Event("storage"));
    },
  };
}

function countUserMessages(messages: ReadonlyArray<{ role: unknown }>): number {
  let count = 0;
  for (const message of messages) {
    if (message.role === "user") count += 1;
  }
  return count;
}

function getLegalNoticeReminderDelay(state: LegalNoticeReminderState): number | null {
  if (state.nextShowAt === null) return null;
  return Math.max(0, state.nextShowAt - Date.now());
}

const PhoebeHomeAvatar: FC = () => (
  <Avatar className="mx-auto size-[50px] shrink-0">
    <AvatarImage src={PHOEBE_AVATAR_SRC} alt="Phoebe" />
    <AvatarFallback>P</AvatarFallback>
  </Avatar>
);

const PhoebeGreeting: FC<{ greeting: string }> = ({ greeting }) => (
  <div className="flex flex-col items-center gap-1 self-stretch text-center">
    <PhoebeHomeAvatar />
    <span
      className="self-stretch text-center font-['Roboto'] text-[12px] font-bold leading-[16px] text-[#5E7079]"
      style={{ fontFeatureSettings: "'liga' off, 'clig' off" }}
    >
      {PHOEBE_LABEL}
    </span>
  </div>
);

const PhoebeGreetingMessage: FC<{ greeting: string }> = ({ greeting }) => (
  <h3
    className="self-stretch text-center font-['Roboto'] text-[24px] font-bold leading-[32px] text-[#001823]"
    style={{ fontFeatureSettings: "'liga' off, 'clig' off" }}
  >
    {greeting}
  </h3>
);

const ThreadWelcome: FC<{ showLegalNotice?: boolean; onComposerSubmit?: () => void }> = ({
  showLegalNotice = true,
  onComposerSubmit,
}) => {
  const greeting = "How can I help?";
  return (
    <div className={cn(THREAD_WELCOME_ROOT_CLASS, "chat-home-gradient")} data-testid="aichat_window_home">
      <div
        className="aui-chat-home-main flex min-h-0 flex-1 flex-col items-center [justify-content:safe_center] gap-10 self-stretch overflow-y-auto p-6"
        aria-live="polite"
        aria-atomic="true"
      >
        <div className="flex max-w-[692px] flex-col items-center justify-center gap-6 self-stretch">
          <div className="flex flex-col items-center gap-[8px] self-stretch">
            <PhoebeHomeAvatar />
            <span
              className="text-center font-['Roboto'] text-[12px] font-bold leading-[16px] text-[#5E7079]"
              style={{ fontFeatureSettings: "'liga' off, 'clig' off" }}
            >
              {PHOEBE_LABEL}
            </span>
            <h3
              className="self-stretch text-center font-['Roboto'] text-[24px] font-bold leading-[32px] text-[#001823]"
              style={{ fontFeatureSettings: "'liga' off, 'clig' off" }}
            >
              {greeting}
            </h3>
          </div>
          <div className="w-full">
            <Composer variant="home" onSubmit={onComposerSubmit} />
          </div>
          <ThreadSuggestions />
        </div>
      </div>
      {showLegalNotice && (
        <div className={THREAD_WELCOME_NOTICE_CLASS}>
          <ChatHomeViewLegalNotice />
        </div>
      )}
    </div>
  );
};

const ThreadSuggestions: FC = () => {
  const { enrollmentConsultationEnabled, genesysLiveChatEnabled } = useRuntimeConfig();
  const visibleSuggestions = HOME_SUGGESTIONS.filter(
    (s) =>
      (s !== "Talk to support" || genesysLiveChatEnabled) &&
      (s !== "Schedule a consultation" || enrollmentConsultationEnabled),
  );
  return (
    <div className="flex w-full flex-wrap justify-center gap-x-2 gap-y-3">
      {visibleSuggestions.map((suggestion) => (
        <ThreadSuggestionItem key={suggestion} text={suggestion} />
      ))}
    </div>
  );
};

// Check the ref-backed guard before assistant-ui clears composer text or sends
// a home suggestion. Reactive pending state also disables the send controls.
export const ComposerSubmissionContext = createContext({
  pending: false,
  canSubmit: (): boolean => true,
});

const ThreadSuggestionItem: FC<{ text: string }> = ({ text }) => {
  const submission = useContext(ComposerSubmissionContext);
  const guardKeyboardSelection = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if ((event.key === "Enter" || event.key === " ") && !submission.canSubmit()) {
      event.preventDefault();
      event.stopPropagation();
    }
  };
  return (
    <div
      className="fade-in slide-in-from-bottom-2 animate-in fill-mode-both duration-200"
      onClickCapture={(event) => {
        if (!submission.canSubmit()) {
          event.preventDefault();
          event.stopPropagation();
        }
      }}
      onKeyDownCapture={guardKeyboardSelection}
      onKeyUpCapture={guardKeyboardSelection}
    >
      <ThreadPrimitive.Suggestion asChild prompt={text} send>
        <Button
          variant="ghost"
          disabled={submission.pending}
          className={cn(
            "aui-suggestion-chip flex h-auto min-h-[32px] items-center self-stretch whitespace-normal rounded-full border border-[#B2BBBF] bg-white px-[10px] py-[4px] font-['Roboto'] text-[13px] font-normal leading-[18px] tracking-[0.16px] text-[#001823] transition-colors hover:bg-[rgba(0,0,0,0.12)] focus-visible:bg-[rgba(0,0,0,0.12)]",
          )}
          style={{ fontFeatureSettings: "'liga' off, 'clig' off" }}
          data-testid="aichat_window_suggestion-chip"
          onClick={() => {
            trackUOPX(UOPX_EVENT.CHIP_CLICK, text, UOPX_LOCATION.CHIPS, UOPX_DATA.CHIP_NAME_HOW_CAN_I_HELP);
          }}
        >
          <span className="min-w-0 whitespace-normal break-words">{text}</span>
        </Button>
      </ThreadPrimitive.Suggestion>
    </div>
  );
};

const Composer: FC<{
  variant?: "home" | "thread";
  inputRef?: Ref<HTMLTextAreaElement>;
  onSubmit?: () => void;
}> = ({
  variant = "thread",
  inputRef,
  onSubmit,
}) => {
  // useComposer re-renders whenever text changes, including when the
  // composer resets to "" after a message is sent.
  const composerText = useComposer((s) => s.text);
  const submission = useContext(ComposerSubmissionContext);
  const charCount = composerText.length;
  const isConnecting = useSocketStatusStore((s) => s.liveAssistance.isConnecting);
  const isEnding = useSocketStatusStore((s) => s.liveAssistance.session?.state === "ending");
  const composerDisabled = isConnecting || isEnding;
  const [isComposerExpanded, setIsComposerExpanded] = useState(false);
  const errorId = useId();
  const hasCharacterLimitError = charCount > MAX_CHARS;
  const showCharacterLimitError = hasCharacterLimitError && !composerDisabled;
  const hasValidValue = composerText.trim().length > 0 && !hasCharacterLimitError && !composerDisabled;
  const isSendBlocked = !hasValidValue || submission.pending;
  const handleComposerKeyDown = (event: ReactKeyboardEvent<HTMLTextAreaElement>) => {
    if (!hasCharacterLimitError || event.key !== "Enter" || event.shiftKey || event.nativeEvent.isComposing) return;
    event.preventDefault();
    event.stopPropagation();
  };

  useEffect(() => {
    if (charCount === 0) setIsComposerExpanded(false);
  }, [charCount]);

  return (
    <ComposerPrimitive.Root
      onSubmitCapture={(event) => {
        if (!submission.canSubmit()) {
          event.preventDefault();
          event.stopPropagation();
        }
      }}
      className={cn(
        COMPOSER_ROOT_CLASS,
        variant === "home" ? HOME_COMPOSER_ROOT_CLASS : THREAD_COMPOSER_ROOT_CLASS,
      )}
    >
      <div
        className={cn(
          COMPOSER_FIELD_CLASS,
          !isComposerExpanded && COMPOSER_FIELD_COMPACT_CLASS,
          showCharacterLimitError && COMPOSER_FIELD_ERROR_CLASS,
          composerDisabled && COMPOSER_FIELD_DISABLED_CLASS,
        )}
        aria-disabled={composerDisabled || undefined}
      >
        <ComposerPrimitive.Input
          ref={inputRef}
          name="input"
          cancelOnEscape={false}
          placeholder={composerDisabled ? "Connecting to specialist..." : "Ask anything"}
          className={cn(
            COMPOSER_INPUT_CLASS,
            isComposerExpanded && COMPOSER_INPUT_EXPANDED_CLASS,
            composerDisabled && COMPOSER_INPUT_DISABLED_CLASS,
          )}
          style={{ outline: "none", boxShadow: "none", fontFeatureSettings: "'liga' off, 'clig' off" }}
          rows={1}
          // ADA (VIAF-391/427): the library defaults yank focus back to this
          // textarea on run start and on every auto-scroll during streaming,
          // making the Stop button and thinking-insight close unreachable by
          // keyboard while a response is generating.
          unstable_focusOnRunStart={false}
          unstable_focusOnScrollToBottom={false}
          onHeightChange={(height) => {
            const nextExpanded = height > COMPOSER_MULTILINE_HEIGHT_THRESHOLD;
            setIsComposerExpanded((current) => (current === nextExpanded ? current : nextExpanded));
          }}
          disabled={composerDisabled}
          maxLength={COMPOSER_INPUT_HARD_MAX_LENGTH}
          onKeyDownCapture={(event) => {
            if (
              event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing &&
              !submission.canSubmit()
            ) {
              event.preventDefault();
              event.stopPropagation();
              return;
            }
            // A keyboard Enter submission needs focus transferred to the
            // replacement composer when the welcome view is replaced. The
            // Send-button path makes the same request in ComposerAction.
            if (
              variant === "home" &&
              event.key === "Enter" &&
              !event.shiftKey &&
              !event.nativeEvent.isComposing &&
              event.currentTarget.value.trim() &&
              !hasCharacterLimitError &&
              !composerDisabled
            ) {
              onSubmit?.();
            }
          }}
          onKeyDown={(event) => {
            handleComposerKeyDown(event);
          }}
          aria-label="Message input"
          aria-invalid={showCharacterLimitError || undefined}
          aria-describedby={showCharacterLimitError ? errorId : undefined}
        />
        <div className={COMPOSER_ACTIONS_CLASS}>
          <ComposerAction
            isActive={hasValidValue && !submission.pending}
            isDisabled={composerDisabled}
            isSendBlocked={isSendBlocked}
            onSubmit={variant === "home" ? onSubmit : undefined}
          />
        </div>
      </div>
      {showCharacterLimitError && (
        <p id={errorId} className={COMPOSER_ERROR_CLASS}>
          <img src={ERROR_FILLED_ICON_SRC} alt="Error" className="size-[16px] shrink-0" />
          Max {MAX_CHARS} characters
        </p>
      )}
      {variant === "thread" && <span className="sr-only">{charCount}/{MAX_CHARS}</span>}
    </ComposerPrimitive.Root>
  );
};

const ComposerAction: FC<{
  isActive?: boolean;
  isDisabled?: boolean;
  isSendBlocked?: boolean;
  onSubmit?: () => void;
}> = ({
  isActive = false,
  isDisabled = false,
  isSendBlocked = false,
  onSubmit,
}) => {
  const { canCancelActiveTurn } = useContext(ThreadActionsContext);
  const submission = useContext(ComposerSubmissionContext);
  const actionStyle = isActive
    ? COMPOSER_ACTION_ACTIVE_STYLE
    : isDisabled || isSendBlocked
      ? COMPOSER_ACTION_DISABLED_STYLE
      : COMPOSER_ACTION_DEFAULT_STYLE;

  return (
    <>
      <AuiIf condition={(s) => !s.thread.isRunning}>
        <ComposerPrimitive.Send asChild>
          <TooltipIconButton
            tooltip="Send"
            side="bottom"
            type="button"
            variant="ghost"
            size="icon"
            className={cn("aui-composer-send", COMPOSER_ACTION_BUTTON_CLASS)}
            style={actionStyle}
            aria-label="Send"
            data-testid="aichat_window_send-message"
            disabled={isSendBlocked}
            onClickCapture={(event) => {
              if (!submission.canSubmit()) {
                event.preventDefault();
                event.stopPropagation();
                return;
              }
              onSubmit?.();
            }}
          >
            <SendOutlinedIcon className="size-[24px]" />
          </TooltipIconButton>
        </ComposerPrimitive.Send>
      </AuiIf>
      <AuiIf condition={(s) => s.thread.isRunning && canCancelActiveTurn}>
        <ComposerPrimitive.Cancel asChild>
          <TooltipIconButton
            tooltip="Stop generating"
            side="bottom"
            type="button"
            variant="ghost"
            size="icon"
            className={cn("aui-composer-cancel", COMPOSER_ACTION_BUTTON_CLASS)}
            style={COMPOSER_ACTION_ACTIVE_STYLE}
            aria-label="Stop generating"
          >
            <StopCircleOutlinedIcon className="size-[24px]" />
          </TooltipIconButton>
        </ComposerPrimitive.Cancel>
      </AuiIf>
    </>
  );
};

const LiveTypingIndicator: FC = () => {
  const typingAgentName = useSocketStatusStore((state) =>
    state.liveAssistance.isTyping ? state.liveAssistance.typingAgentName ?? "Support Specialist" : null,
  );
  if (!typingAgentName) return null;

  const currentTime = formatMessageTime(new Date());

  return (
    <div
      className="mx-auto flex w-[376px] max-w-[calc(100%-16px)] items-start gap-[12px]"
      aria-live="polite"
      aria-label={`${typingAgentName} is typing`}
      data-testid="aichat_window_live-agent-typing"
    >
      <span className="sr-only">{typingAgentName} is typing</span>
      
      {/* Avatar placeholder - matches live agent avatar styling */}
      <div
        className="size-7 shrink-0 self-start mt-0.5 rounded-full bg-[#B2BBBF] flex items-center justify-center"
        aria-hidden="true"
      >
        <span className="text-white text-xs font-semibold" aria-hidden="true">
          {typingAgentName.charAt(0).toUpperCase()}
        </span>
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-[4px]">
        {/* Agent name and timestamp */}
        <div className="flex min-w-0 items-center gap-[4px] font-['Roboto',sans-serif] text-[12px] leading-[16px] tracking-[0px] text-[#5e7079]">
          <span className="min-w-0 truncate font-bold">{typingAgentName}</span>
          <span className="size-[4px] shrink-0 rounded-full bg-[#5e7079]" aria-hidden="true" />
          <time className="shrink-0 font-normal">{currentTime}</time>
        </div>

        {/* Typing indicator row */}
        <div className="flex min-w-0 items-center gap-2">
          <span className="font-['Roboto',sans-serif] text-[14px] font-normal leading-[20px] tracking-[0px] text-[#001823]" aria-hidden="true">
            Typing
          </span>
          
          {/* Three animated dots with specific colors */}
          <span className="flex h-[8px] items-center gap-[4px]" aria-hidden="true">
            <span
              className="inline-block size-[6px] rounded-full bg-[#1A1A1A]"
              style={{
                animation: "typing-dot-bounce 1.2s ease-in-out infinite",
                animationDelay: "0s",
              }}
            />
            <span
              className="inline-block size-[6px] rounded-full bg-[#333333]"
              style={{
                animation: "typing-dot-bounce 1.2s ease-in-out infinite",
                animationDelay: "0.15s",
              }}
            />
            <span
              className="inline-block size-[6px] rounded-full bg-[#666666]"
              style={{
                animation: "typing-dot-bounce 1.2s ease-in-out infinite",
                animationDelay: "0.3s",
              }}
            />
          </span>

          {/* Dotted line extending to the right - SVG based with animation */}
          <svg
            className="flex-1 min-w-0"
            height="2"
            viewBox="0 0 245 2"
            fill="none"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <path
              d="M244.528 0.50012L0 0.677734"
              stroke="#3E3E3E"
              strokeWidth="1"
              strokeDasharray="2 2"
              style={{
                animation: "typing-dash-flow 1s linear infinite",
              }}
            />
          </svg>
        </div>
      </div>
    </div>
  );
};

const MessageError: FC = () => (
  <MessagePrimitive.Error>
    <ErrorPrimitive.Root
      className="aui-message-error-root mt-2 flex w-full max-w-[367px] items-center gap-2 py-1 font-['Roboto',sans-serif] text-[14px] leading-[20px] font-normal tracking-[0px] text-[#b21f13]"
      role="alert"
      data-testid="aichat_window_error_response"
    >
      <img src={ERROR_FILLED_ICON_SRC} alt="Error" className="size-[16.667px] shrink-0" />
      <span className="min-w-0 flex-1 wrap-break-word" style={{ fontFeatureSettings: "'liga' off, 'clig' off" }}>
        I'm having trouble connecting right now. Please try again or call 1-866-766-0766 for additional assistance.
      </span>
      <ErrorPrimitive.Message className="sr-only" />
    </ErrorPrimitive.Root>
  </MessagePrimitive.Error>
);

/**
 * Shows Thinking while the backend is running but no text has arrived yet,
 * then switches to the real message content once the first chunk lands.
 * Uses useMessage (must be called inside MessagePrimitive.Root).
 */
const AssistantMessageContent: FC = () => {
  const isRunning = useMessage((s) => s.role === "assistant" && s.status?.type === "running");
  const messageId = useMessage((s) => s.id);
  const isThreadRunning = useThread((s) => s.isRunning);
  const isLatestMessage = useThread((s) => s.messages.at(-1)?.id === messageId);
  const hasText = useMessage((s) =>
    s.content.some(
      (c): c is { type: "text"; text: string } =>
        c.type === "text" && (c as { type: "text"; text: string }).text.length > 0,
    ),
  );

  if (isRunning && (!isThreadRunning || !isLatestMessage) && !hasText) {
    return null;
  }

  if (isRunning && !hasText) {
    return <AssistantThinkingMessage />;
  }

  return (
    <>
      <SuperPhoebeMessageParts
        components={{
          Text: MarkdownText,
          tools: { Fallback: ToolFallback },
        }}
      />
      {isRunning && isThreadRunning && hasText ? (
        <img
          src={LOADER_ICON_SRC}
          alt=""
          className="mt-1 block size-[16px] shrink-0 animate-spin [animation-duration:1.2s]"
          aria-hidden="true"
          data-testid="aichat_window_agent-streaming"
        />
      ) : null}
    </>
  );
};

const AssistantThinkingMessage: FC = () => {
  const { thinkingInsightsEnabled } = useRuntimeConfig();
  const socketStageMessage = useSocketStatusStore((state) => getSocketStageMessage(state.activeTurn));
  const isErrorMessage = useSocketStatusStore((state) => Boolean(state.activeTurn?.error));
  const [facts, setFacts] = useState(EMPTY_THINKING_INSIGHTS);
  const [messageIndex, setMessageIndex] = useState(0);
  const [isFading, setIsFading] = useState(false);
  const [canShowInsights, setCanShowInsights] = useState(false);
  const [isInsightDismissed, setIsInsightDismissed] = useState(hasDismissedThinkingInsights);
  const fadeTimeoutRef = useRef<number | null>(null);
  const supportiveMessage = facts.length > 0 ? facts[messageIndex % facts.length] : null;
  const displayMessage = "Thinking";
  const accessibleStatusMessage = socketStageMessage ?? "Thinking";
  const showSupportiveLine = Boolean(
    thinkingInsightsEnabled && canShowInsights && !isErrorMessage && !isInsightDismissed && supportiveMessage,
  );
  const screenReaderStatusMessage =
    showSupportiveLine && supportiveMessage
      ? `${accessibleStatusMessage}. University of Phoenix fact: ${supportiveMessage}`
      : accessibleStatusMessage;

  const handleDismissInsight = useCallback(() => {
    writeThinkingInsightsDismissed();
    setIsInsightDismissed(true);
    setCanShowInsights(false);
  }, []);

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key === THINKING_INSIGHTS_DISMISSED_STORAGE_KEY) {
        setIsInsightDismissed(syncThinkingInsightsDismissedFromStorage());
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  useEffect(() => {
    setCanShowInsights(false);
    if (!thinkingInsightsEnabled || isInsightDismissed) return;

    const delay = window.setTimeout(() => {
      setCanShowInsights(true);
    }, THINKING_INSIGHTS_DELAY_MS);

    return () => {
      window.clearTimeout(delay);
    };
  }, [isInsightDismissed, thinkingInsightsEnabled]);

  useEffect(() => {
    setFacts(thinkingInsightsEnabled ? [...LEGAL_APPROVED_THINKING_INSIGHTS] : EMPTY_THINKING_INSIGHTS);
    setMessageIndex(0);
  }, [thinkingInsightsEnabled]);

  useEffect(() => {
    if (isErrorMessage || !showSupportiveLine || facts.length < 2) {
      setIsFading(false);
      return;
    }

    let alive = true;
    const interval = window.setInterval(() => {
      setIsFading(true);

      fadeTimeoutRef.current = window.setTimeout(() => {
        if (!alive) return;
        setMessageIndex((currentIndex) => randomThinkingInsightIndex(facts.length, currentIndex));
        setIsFading(false);
      }, THINKING_MESSAGE_FADE_MS);
    }, THINKING_MESSAGE_INTERVAL_MS);

    return () => {
      alive = false;
      window.clearInterval(interval);

      if (fadeTimeoutRef.current !== null) {
        window.clearTimeout(fadeTimeoutRef.current);
      }
    };
  }, [facts.length, isErrorMessage, showSupportiveLine]);

  return (
    <div
      className="aui-assistant-thinking-message flex min-h-6 min-w-0 flex-col items-start gap-[12px] self-stretch py-1"
      data-testid="aichat_window_agent-thinking"
    >
      <span
        className="sr-only"
        role="status"
        aria-live="polite"
        aria-atomic="true"
        data-testid="aichat_window_agent-thinking_accessible-status"
      >
        {screenReaderStatusMessage}
      </span>
      <span
        aria-hidden="true"
        className={cn(
          "flex min-w-0 flex-col gap-0.5 font-['Roboto',sans-serif] text-[14px] font-normal leading-[20px] tracking-[0px] text-[#001823] transition-opacity duration-200",
          "opacity-100",
        )}
        style={{ fontFeatureSettings: "'liga' off, 'clig' off" }}
      >
        <span className="flex min-w-0 items-center gap-[8px]">
          <span data-testid="aichat_window_agent-thinking_label">{displayMessage}</span>
          <img
            src={LOADER_ICON_SRC}
            alt=""
            className="size-[16px] shrink-0 animate-spin [animation-duration:1.2s]"
            aria-hidden="true"
            data-testid="aichat_window_agent-thinking_loader"
          />
        </span>
      </span>
      {showSupportiveLine ? (
        <span
          data-testid="aichat_window_thinking-insight"
          role="status"
          aria-live="polite"
          aria-atomic="true"
          className={cn(
            "-ml-px flex w-[336px] max-w-[calc(100dvw-71px)] flex-col items-start justify-center overflow-hidden rounded-[8px] border border-[rgba(0,24,35,0.12)] bg-white font-['Roboto',sans-serif] text-[12px] font-normal leading-[16px] tracking-[0px] text-[#5e7079] transition-opacity duration-200",
            isFading ? "opacity-0" : "opacity-100",
          )}
        >
          <span className="flex items-center gap-[6px] self-stretch bg-[#FAFAFA] py-[4px] pl-[8px] pr-[4px]">
            <img
              src={WB_INCANDESCENT_OUTLINED_SRC}
              alt=""
              aria-hidden="true"
              className="size-[16px] shrink-0"
              data-testid="aichat_window_thinking-insight_bulb"
            />
            <span className="font-medium text-[#5e7079]" aria-hidden="true">
              Did you know?
            </span>
            <button
              type="button"
              data-testid="aichat_window_did-you-know_close"
              className="aui-did-you-know-close ml-auto flex size-5 shrink-0 items-center justify-center rounded-full text-[rgba(0,24,35,0.56)] transition-colors hover:bg-[rgba(0,24,35,0.08)] hover:text-[#001823] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#001823] focus-visible:ring-offset-2 disabled:text-[rgba(0,24,35,0.38)]"
              aria-label="Close did you know"
              onClick={handleDismissInsight}
            >
              <CloseOutlinedIcon
                className="size-3"
                aria-hidden="true"
                data-testid="aichat_window_thinking-insight_dismiss"
              />
            </button>
          </span>
          <span
            className="flex items-center justify-center gap-[8px] self-stretch px-[16px] py-[12px] text-[#5e7079]"
            data-testid="aichat_window_thinking-insight_body"
          >
            {supportiveMessage ?? ""}
          </span>
        </span>
      ) : null}
    </div>
  );
};

const PostAnswerSuggestions: FC = () => {
  const { backendUrl, isVisible } = useContext(ThreadTelemetryContext);
  const aui = useAui();
  const messageId = useMessage((message) => message.id);
  const rawFollowUpSuggestions = useMessage((message) => {
    if (message.role !== "assistant" || message.status?.type === "running") return null;
    if (liveAssistanceMetadata(message.metadata)?.kind) return null;
    const custom = (message.metadata as { custom?: Record<string, unknown> } | undefined)?.custom;
    const followUpSuggestions = custom?.followUpSuggestions;
    return Array.isArray(followUpSuggestions) ? followUpSuggestions : null;
  });
  const clientMessageId = useMessage((message) => {
    const custom = (message.metadata as { custom?: Record<string, unknown> } | undefined)?.custom;
    return typeof custom?.clientMessageId === "string" ? custom.clientMessageId : undefined;
  });
  const traceId = useMessage((message) => {
    const custom = (message.metadata as { custom?: Record<string, unknown> } | undefined)?.custom;
    return typeof custom?.traceId === "string" ? custom.traceId : undefined;
  });
  const suggestions = useMemo(() => {
    if (!rawFollowUpSuggestions) return EMPTY_FOLLOW_UP_SUGGESTIONS;
    return rawFollowUpSuggestions.filter(isFollowUpSuggestion);
  }, [rawFollowUpSuggestions]);
  const asyncSuggestionsMap = useContext(AsyncSuggestionsContext);
  const asyncSuggestions = clientMessageId ? asyncSuggestionsMap.get(clientMessageId) : undefined;
  const displaySuggestions = useMemo(
    () => (suggestions.length > 0 ? suggestions : (asyncSuggestions ?? EMPTY_FOLLOW_UP_SUGGESTIONS)),
    [asyncSuggestions, suggestions],
  );
  const isLatestMessage = useThread((state) => state.messages.at(-1)?.id === messageId);
  const impressionKey = `${messageId}:${displaySuggestions.map((suggestion) => suggestion.id).join("|")}`;
  const lastImpressionKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!backendUrl || !isVisible || !isLatestMessage || displaySuggestions.length === 0) return;
    if (lastImpressionKeyRef.current === impressionKey) return;
    lastImpressionKeyRef.current = impressionKey;
    displaySuggestions.forEach((suggestion, index) => {
      sendWidgetTelemetry({
        backendUrl,
        eventName: "suggestion_chip_impression",
        clientMessageId,
        traceId,
        suggestionId: suggestion.id,
        suggestionCategory: suggestion.category,
        suggestionPosition: index,
      });
    });
  }, [backendUrl, clientMessageId, impressionKey, isLatestMessage, isVisible, displaySuggestions, traceId]);

  if (!isLatestMessage || displaySuggestions.length === 0) return null;

  return (
    <div className={POST_ANSWER_SUGGESTIONS_CLASS} aria-label="Suggested follow-up questions">
      {displaySuggestions.map((suggestion, index) => (
        <Button
          key={suggestion.id}
          type="button"
          variant="ghost"
          className={POST_ANSWER_SUGGESTION_BUTTON_CLASS}
          data-testid="aichat_window_suggestion-chip"
          onClick={() => {
            if (aui.thread().getState().isRunning) return;
            // Track chip click for UOPX DataLayer
            trackUOPX(UOPX_EVENT.CHIP_CLICK, suggestion.label, UOPX_LOCATION.CHIPS, UOPX_DATA.CHIP_NAME_HOW_CAN_I_HELP);
            if (backendUrl) {
              sendWidgetTelemetry({
                backendUrl,
                eventName: "suggestion_chip_selected",
                clientMessageId,
                traceId,
                suggestionId: suggestion.id,
                suggestionCategory: suggestion.category,
                suggestionPosition: index,
              });
            }
            aui.thread().append({
              content: [{ type: "text", text: suggestion.label }],
            });
          }}
        >
          <span className="block max-w-full whitespace-normal break-words text-left [overflow-wrap:anywhere]">
            {suggestion.label}
          </span>
        </Button>
      ))}
    </div>
  );
};

const AssistantMessage: FC = () => {
  const kind = useMessage((message) => liveAssistanceMetadata(message.metadata)?.kind);
  const { genesysLiveChatQueueDisplayEnabled } = useRuntimeConfig();
  const isEmptyCancelledMessage = useMessage((message) =>
    message.status?.type === "incomplete" &&
    message.status.reason === "cancelled" &&
    !hasVisibleMessageContent(message.content),
  );
  
  // Filter out messages with handoffSignal metadata - these are Phoebe responses
  // that triggered a handoff and shouldn't be shown (the handoff_marker replaces them)
  const hasHandoffSignal = useMessage((message) => {
    const custom = (message.metadata as { custom?: Record<string, unknown> } | undefined)?.custom;
    return custom?.handoffSignal !== undefined;
  });
  
  if (isEmptyCancelledMessage || hasHandoffSignal) {
    return null;
  }

  // Gate queue_status messages behind the feature flag
  if (kind === "queue_status" && !genesysLiveChatQueueDisplayEnabled) {
    return null;
  }
  
  if (["handoff_marker", "ended", "error", "handoff_failed", "queue_status"].includes(kind ?? "")) {
    return (
      <>
        <MessageBoundaryMarkers />
        <LiveSystemMessage />
      </>
    );
  }
  if (kind === "system_message") {
    return (
      <>
        <MessageBoundaryMarkers />
        <LiveQueueMessage />
      </>
    );
  }
  if (kind === "agent_message") {
    return (
      <>
        <MessageBoundaryMarkers />
        <LiveRepresentativeMessage />
      </>
    );
  }

  return (
    <>
      <MessageBoundaryMarkers />
      <MessagePrimitive.Root
        className={ASSISTANT_MESSAGE_ROOT_CLASS}
        data-role="assistant"
        data-testid="aichat_window_message-phoebe"
      >
        <AssistantMessageAvatar />

        <div className="flex min-w-0 flex-[1_0_0] flex-col items-start gap-[8px]">
          <AssistantMessageLabel />
          <AssistantMessageBubble>
            <AssistantMessageContent />
            <MessageError />
          </AssistantMessageBubble>
          <SafetyExitPrompt />
          <EnrollmentConsultationCard />
          <PostAnswerSuggestions />

          <AssistantMessageActions />
        </div>
      </MessagePrimitive.Root>
    </>
  );
};

/**
 * Returns true once the first standard (non-handoff, non-live) assistant
 * message exists and has finished streaming. Used by ThreadFooterLegalNotice
 * to gate the initial disclaimer so it only appears after Phoebe's first
 * completed response, not while the assistant turn is still running.
 */
function useHasFirstCompletedAssistantMessage(): boolean {
  return useThread((state) => {
    for (const message of state.messages) {
      if (message.role !== "assistant") continue;
      const custom = (message.metadata as { custom?: Record<string, unknown> } | undefined)?.custom;
      if (custom?.handoffSignal !== undefined) continue;
      if (liveAssistanceMetadata(message.metadata)?.kind) continue;
      // Found the first standard assistant message — only count it when not running
      return message.status?.type !== "running";
    }
    return false;
  });
}

/**
 * ThreadFooterLegalNotice — renders the dismissible legal notice as a fixed footer
 * between the scroll viewport and the composer. The reminder clock starts with
 * the thread, but the notice cannot render before the first completed response.
 */
const ThreadFooterLegalNotice: FC = () => {
  const hasFirstCompletedAssistantMessage = useHasFirstCompletedAssistantMessage();

  return (
    <div className={THREAD_FOOTER_NOTICE_CLASS}>
      <ThreadLegalNoticeReminder canDisplay={hasFirstCompletedAssistantMessage} />
    </div>
  );
};

const LiveRepresentativeMessage: FC = () => {
  const timestamp = useMessageTime();
  const agentName = useMessage((message) =>
    liveAssistanceMetadata(message.metadata)?.agentName ?? "Support Specialist",
  );

  return (
    <MessagePrimitive.Root
      className={LIVE_REPRESENTATIVE_MESSAGE_CLASS}
      data-role="assistant"
      data-testid="aichat_window_message-agent"
    >
      <LiveAgentAvatar agentName={agentName} />
      <div className={LIVE_REPRESENTATIVE_MAIN_CLASS}>
        <span className={cn("aui-live-representative-label", LIVE_REPRESENTATIVE_LABEL_CLASS)}>
          <span className="min-w-0 truncate font-bold">{agentName}</span>
          <span className="aui-live-representative-label-dot size-[4px] shrink-0 rounded-full bg-[#5e7079]" aria-hidden="true" />
          <time className="shrink-0 font-normal">{timestamp}</time>
        </span>
        <div className={LIVE_REPRESENTATIVE_CONTENT_CLASS} style={CHAT_MESSAGE_BODY_TEXT_STYLE}>
          <AssistantMessageContent />
          <MessageError />
        </div>
      </div>
    </MessagePrimitive.Root>
  );
};

const LiveQueueMessage: FC = () => {
  const timestamp = useMessageTime();
  return (
    <MessagePrimitive.Root
      className="aui-live-queue-message-root fade-in slide-in-from-bottom-1 mx-auto flex w-[376px] max-w-[calc(100%-16px)] animate-in flex-col items-center gap-1 py-2 text-center font-['Roboto',sans-serif] text-[12px] leading-[16px] font-normal tracking-[0px] text-[#5e7079] duration-150"
      data-role="assistant"
      data-testid="aichat_window_system-message_queue"
    >
      <span className="max-w-full wrap-break-word">
        <MessagePrimitive.Parts />
      </span>
      <time>{timestamp}</time>
    </MessagePrimitive.Root>
  );
};

const LiveSystemMessage: FC = () => {
  const kind = useMessage((message) => liveAssistanceMetadata(message.metadata)?.kind);
  const timestamp = useMessageTime();

  return (
    <MessagePrimitive.Root
      className="aui-live-handoff-marker-root fade-in slide-in-from-bottom-1 flex animate-in flex-col items-center gap-1 self-stretch duration-150"
      data-role="assistant"
      {...(kind === "handoff_marker" ? { "data-testid": "aichat_window_system-message_live-agent-connected" } : {})}
      {...(kind === "queue_status" ? { "data-testid": "aichat_window_system-message_queue-status" } : {})}
      {...(kind === "ended" ? { "data-testid": "aichat_window_live-agent-chat_ended" } : {})}
    >
      <div 
        className="whitespace-pre-line text-balance text-center font-['Roboto',sans-serif] text-[12px] font-normal leading-[16px] tracking-[0px] text-[#5E7079]"
        style={{ fontFeatureSettings: "'liga' off, 'clig' off" }}
      >
        <MessagePrimitive.Parts />
      </div>
      <time 
        className="text-center font-['Roboto',sans-serif] text-[12px] font-normal leading-[16px] tracking-[0px] text-[#5E7079]"
        style={{ fontFeatureSettings: "'liga' off, 'clig' off" }}
      >
        {timestamp}
      </time>
    </MessagePrimitive.Root>
  );
};

const AssistantMessageAvatar: FC = () => {
  const agentName = useMessage((message) => {
    const liveAssistance = liveAssistanceMetadata(message.metadata);
    return liveAssistance?.kind === "agent_message" ? liveAssistance.agentName ?? "Support Specialist" : null;
  });

  return agentName ? <LiveAgentAvatar agentName={agentName} /> : <PhoebeAvatar />;
};

const AssistantMessageLabel: FC = () => {
  const timestamp = useMessageTime();
  const label = useMessage((message) => {
    const liveAssistance = liveAssistanceMetadata(message.metadata);
    return liveAssistance?.kind === "agent_message"
      ? liveAssistance.agentName ?? "Support Specialist"
      : PHOEBE_LABEL;
  });

  return (
    <span className="mb-1 flex min-w-0 items-center gap-1 text-xs font-semibold text-muted-foreground">
      <span className="truncate">{label}</span>
      <span aria-hidden="true" className="inline-block shrink-0" style={USER_MESSAGE_DOT_SEPARATOR_STYLE} />
      <time className="shrink-0 font-normal">{timestamp}</time>
    </span>
  );
};

const AssistantMessageBubble: FC<{ children: ReactNode }> = ({ children }) => {
   return (
    <div
      className="aui-assistant-message-content wrap-break-word"
      style={CHAT_MESSAGE_BODY_TEXT_STYLE}
    >
      {children}
    </div>
  );
};

const SafetyExitPrompt: FC = () => {
  const { onSafetyExit } = useContext(ThreadActionsContext);
  const descriptionId = useId();
  const shouldShow = useMessage((message) => {
    if (message.role !== "assistant" || message.status?.type === "running") return false;
    if (liveAssistanceMetadata(message.metadata)?.kind) return false;
    return safetyExitMetadata(message.metadata)?.kind === "triggered";
  });

  if (!shouldShow || !onSafetyExit) return null;

  return (
    <div className={SAFETY_EXIT_PROMPT_CLASS}>
      <Button
        type="button"
        variant="ghost"
        className={SAFETY_EXIT_BUTTON_CLASS}
        aria-describedby={descriptionId}
        data-testid="aichat_window_safety-exit"
        onClick={onSafetyExit}
      >
        Safely exit chat
      </Button>
      <p id={descriptionId} className={SAFETY_EXIT_DESCRIPTION_CLASS}>
        This will clear your entire chat history.
      </p>
    </div>
  );
};

const EnrollmentConsultationCard: FC = () => {
  const { enrollmentConsultationEnabled } = useRuntimeConfig();
  const metadata = useMessage((message) => enrollmentConsultationMetadata(message.metadata));
  const isRunning = useMessage((message) => message.status?.type === "running");
  const timeOptionsRef = useRef<ConsultationTimeOption[] | undefined>(undefined);
  if (!timeOptionsRef.current) {
    timeOptionsRef.current = createSampleConsultationTimeOptions();
  }
  const timeOptions = timeOptionsRef.current;
  const [step, setStep] = useState<EnrollmentConsultationStep>("offer");
  const [contactMethod, setContactMethod] = useState<ConsultationContactMethod>("phone");
  const [selectedTimeId, setSelectedTimeId] = useState(() => timeOptions[0]?.id ?? "");

  if (!enrollmentConsultationEnabled || !metadata || isRunning) return null;

  const selectedTime = timeOptions.find((option) => option.id === selectedTimeId) ?? timeOptions[0]!;
  const selectedTimeIndex = Math.max(0, timeOptions.findIndex((option) => option.id === selectedTime.id));

  if (step === "selected") {
    return (
      <article
        className="aui-enrollment-consultation-card mt-3 grid w-[320px] max-w-full gap-3 rounded-[8px] border border-[#b7d9e4] bg-[#f6fcfa] p-[14px] shadow-[0_8px_18px_rgba(0,24,35,0.07)]"
        aria-label="Consultation time selection"
        data-testid="aichat_window_enrollment-consultation-selection"
      >
        <div className="grid grid-cols-[34px_minmax(0,1fr)] items-start gap-[10px]">
          <div
            className="grid size-[34px] place-items-center rounded-[8px] bg-[#e7f7ef] text-[#1d7d53]"
            aria-hidden="true"
          >
            <CheckIcon className="size-[18px] stroke-[2.25]" />
          </div>
          <div className="min-w-0">
            <h4 className="m-0 font-['Roboto',sans-serif] text-[15px] leading-[20px] font-bold tracking-[0px] text-[#001823]">
              Consultation time selected
            </h4>
            <p className="m-0 mt-[2px] font-['Roboto',sans-serif] text-[12px] leading-[16px] tracking-[0px] text-[#5e7079]">
              Preview only. No appointment booked yet.
            </p>
          </div>
        </div>
        <ul className="m-0 grid list-none gap-[6px] p-0 font-['Roboto',sans-serif] text-[12px] leading-[16px] tracking-[0px] text-[#001823]">
          <ConsultationMetaItem>{selectedTime.confirmationDate}</ConsultationMetaItem>
          <ConsultationMetaItem>{selectedTime.timeLabel}</ConsultationMetaItem>
          <ConsultationMetaItem>
            {contactMethod === "phone" ? "Phone" : "Video"} consultation, 20 minutes
          </ConsultationMetaItem>
        </ul>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="ghost"
            className="h-[34px] rounded-full border border-[#8ebfce] bg-white px-[14px] py-0 font-['Roboto',sans-serif] text-[13px] leading-[18px] font-bold tracking-[0px] text-[#20556b] hover:bg-[#f2fbfd] focus-visible:ring-2 focus-visible:ring-[#001823] focus-visible:ring-offset-2"
            onClick={() => setStep("picker")}
          >
            Change time
          </Button>
        </div>
      </article>
    );
  }

  if (step === "picker") {
    return (
      <article
        className="aui-enrollment-consultation-card mt-3 grid w-[320px] max-w-full gap-3 rounded-[8px] border border-[#b7d9e4] bg-[#fbfeff] p-[14px] shadow-[0_8px_18px_rgba(0,24,35,0.07)]"
        aria-label="Consultation time picker"
        data-testid="aichat_window_enrollment-consultation-picker"
      >
        <div
          className="flex w-fit overflow-hidden rounded-full border border-[rgba(0,24,35,0.14)] bg-white p-[2px]"
          role="radiogroup"
          aria-label="Contact method"
        >
          <button
            type="button"
            role="radio"
            aria-checked={contactMethod === "phone"}
            tabIndex={contactMethod === "phone" ? 0 : -1}
            className={cn(
              "rounded-full px-3 py-[5px] font-['Roboto',sans-serif] text-[12px] leading-[16px] tracking-[0px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#001823] focus-visible:ring-offset-2",
              contactMethod === "phone" ? "bg-[#e9f6fa] font-bold text-[#20556b]" : "text-[#5e7079] hover:bg-[#f6fbfd]",
            )}
            onClick={() => setContactMethod("phone")}
            onKeyDown={(event) => {
              if (event.key === "ArrowRight" || event.key === "ArrowDown" || event.key === "End") {
                event.preventDefault();
                setContactMethod("video");
                focusRadiogroupOption(event, 1);
              }
            }}
          >
            Phone
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={contactMethod === "video"}
            tabIndex={contactMethod === "video" ? 0 : -1}
            className={cn(
              "rounded-full px-3 py-[5px] font-['Roboto',sans-serif] text-[12px] leading-[16px] tracking-[0px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#001823] focus-visible:ring-offset-2",
              contactMethod === "video" ? "bg-[#e9f6fa] font-bold text-[#20556b]" : "text-[#5e7079] hover:bg-[#f6fbfd]",
            )}
            onClick={() => setContactMethod("video")}
            onKeyDown={(event) => {
              if (event.key === "ArrowLeft" || event.key === "ArrowUp" || event.key === "Home") {
                event.preventDefault();
                setContactMethod("phone");
                focusRadiogroupOption(event, 0);
              }
            }}
          >
            Video
          </button>
        </div>
        <fieldset className="m-0 grid gap-2 border-0 p-0">
          <legend className="sr-only">Choose a consultation time</legend>
          <div className="grid gap-2" role="radiogroup" aria-label="Choose a consultation time">
            {timeOptions.map((option) => {
              const selected = selectedTimeId === option.id;
              return (
                <button
                  key={option.id}
                  type="button"
                  role="radio"
                  className={cn(
                    "flex min-h-[58px] w-full items-center justify-between gap-3 rounded-[8px] border bg-white px-3 py-[10px] text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#001823] focus-visible:ring-offset-2",
                    selected
                      ? "border-[#30718d] bg-[#f2fbfd]"
                      : "border-[rgba(0,24,35,0.14)] hover:border-[#8ebfce]",
                  )}
                  aria-checked={selected}
                  tabIndex={selected ? 0 : -1}
                  onClick={() => setSelectedTimeId(option.id)}
                  onKeyDown={(event) => {
                    let nextIndex: number | undefined;

                    if (event.key === "ArrowDown" || event.key === "ArrowRight") {
                      nextIndex = (selectedTimeIndex + 1) % timeOptions.length;
                    }
                    if (event.key === "ArrowUp" || event.key === "ArrowLeft") {
                      nextIndex = (selectedTimeIndex - 1 + timeOptions.length) % timeOptions.length;
                    }
                    if (event.key === "Home") {
                      nextIndex = 0;
                    }
                    if (event.key === "End") {
                      nextIndex = timeOptions.length - 1;
                    }
                    if (nextIndex === undefined) return;

                    event.preventDefault();
                    setSelectedTimeId(timeOptions[nextIndex]?.id ?? selectedTime.id);
                    focusRadiogroupOption(event, nextIndex);
                  }}
                >
                  <span className="min-w-0">
                    <span className="block font-['Roboto',sans-serif] text-[13px] leading-[18px] font-bold tracking-[0px] text-[#001823]">
                      {option.dateLabel}
                    </span>
                    <span className="block font-['Roboto',sans-serif] text-[12px] leading-[16px] tracking-[0px] text-[#5e7079]">
                      {option.timeLabel}
                    </span>
                  </span>
                  {selected && (
                    <span
                      className="grid size-[22px] shrink-0 place-items-center rounded-full bg-[#30718d] text-white"
                      aria-hidden="true"
                    >
                      <CheckIcon className="size-[14px] stroke-[3]" />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </fieldset>
        <ConsultationPrimaryButton type="button" onClick={() => setStep("selected")}>
          Select this time
        </ConsultationPrimaryButton>
      </article>
    );
  }

  return (
    <article
      className="aui-enrollment-consultation-card mt-3 grid w-[320px] max-w-full gap-3 rounded-[8px] border border-[#b7d9e4] bg-[#fbfeff] p-[14px] shadow-[0_8px_18px_rgba(0,24,35,0.07)]"
      aria-label="Enrollment advisor consultation"
      data-testid="aichat_window_enrollment-consultation-card"
    >
      <div className="grid grid-cols-[34px_minmax(0,1fr)] items-start gap-[10px]">
        <div
          className="grid size-[34px] place-items-center rounded-[8px] bg-[#e9f6fa] text-[#30718d]"
          aria-hidden="true"
        >
          <CalendarDaysIcon className="size-[18px] stroke-[2.25]" />
        </div>
        <div className="min-w-0">
          <h4 className="m-0 font-['Roboto',sans-serif] text-[15px] leading-[20px] font-bold tracking-[0px] text-[#001823]">
            Enrollment advisor consultation
          </h4>
          <p className="m-0 mt-[2px] font-['Roboto',sans-serif] text-[12px] leading-[16px] tracking-[0px] text-[#5e7079]">
            20 minute phone or video conversation
          </p>
        </div>
      </div>
      <ul className="m-0 grid list-none gap-[6px] p-0 font-['Roboto',sans-serif] text-[12px] leading-[16px] tracking-[0px] text-[#001823]">
        <ConsultationMetaItem>Program fit and start options</ConsultationMetaItem>
        <ConsultationMetaItem>Admissions and financial questions</ConsultationMetaItem>
        <ConsultationMetaItem>Next steps based on your goals</ConsultationMetaItem>
      </ul>
      <ConsultationPrimaryButton type="button" onClick={() => setStep("picker")}>
        Choose a time
      </ConsultationPrimaryButton>
    </article>
  );
};

const ConsultationMetaItem: FC<{ children: ReactNode }> = ({ children }) => (
  <li className="flex min-w-0 items-center gap-2">
    <span className="size-[5px] shrink-0 rounded-full bg-[#30718d]" aria-hidden="true" />
    <span className="min-w-0 wrap-break-word">{children}</span>
  </li>
);

const ConsultationPrimaryButton: FC<ButtonHTMLAttributes<HTMLButtonElement>> = ({
  className,
  ...props
}) => (
  <Button
    variant="ghost"
    className={cn(
      "h-9 w-fit rounded-full border-0 bg-[#30718d] px-4 py-0 font-['Roboto',sans-serif] text-[13px] leading-[18px] font-bold tracking-[0px] text-white hover:bg-[#20556b] hover:text-white focus-visible:ring-2 focus-visible:ring-[#001823] focus-visible:ring-offset-2",
      className,
    )}
    {...props}
  />
);

function createSampleConsultationTimeOptions(now = new Date()): ConsultationTimeOption[] {
  const phoenixToday = getPhoenixCalendarDate(now);
  return CONSULTATION_SAMPLE_TIMES.map((sample) => {
    const date = addPhoenixCalendarDays(phoenixToday, sample.dayOffset);
    return {
      id: sample.id,
      dateLabel: formatConsultationDate(date, { weekday: "short", month: "short", day: "numeric" }),
      timeLabel: sample.timeLabel,
      confirmationDate: formatConsultationDate(date, {
        weekday: "short",
        month: "short",
        day: "numeric",
        year: "numeric",
      }),
    };
  });
}

function getPhoenixCalendarDate(date: Date): { year: number; month: number; day: number } {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: CONSULTATION_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);
  return {
    year: Number(parts.find((part) => part.type === "year")?.value),
    month: Number(parts.find((part) => part.type === "month")?.value),
    day: Number(parts.find((part) => part.type === "day")?.value),
  };
}

function addPhoenixCalendarDays(date: { year: number; month: number; day: number }, days: number): Date {
  return new Date(Date.UTC(date.year, date.month - 1, date.day + days, 12));
}

function formatConsultationDate(date: Date, options: Intl.DateTimeFormatOptions): string {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: CONSULTATION_TIME_ZONE,
    ...options,
  }).format(date);
}

const AssistantMessageActions: FC = () => {
  const isLiveAssistanceMessage = useMessage(
    (message) => Boolean(liveAssistanceMetadata(message.metadata)?.kind),
  );
  const isSafetyExitMessage = useMessage(
    (message) => safetyExitMetadata(message.metadata)?.kind === "triggered",
  );
  if (isLiveAssistanceMessage) return <div className="mt-1 min-h-4" aria-hidden="true" />;

  if (isSafetyExitMessage) {
    return (
      <div className="mt-1 flex min-h-4 items-center gap-1">
        <AssistantRatingControls />
      </div>
    );
  }

  return (
    <div className="mt-1 flex min-h-4 items-center gap-1">
      <AssistantRatingControls />
      <BranchPicker />
    </div>
  );
};

const AssistantRatingControls: FC = () => {
  const isRunning = useMessage((message) => message.status?.type === "running");
  const messageMetadata = useMessage((message) => message.metadata);
  const feedbackMetadata = assistantFeedbackMetadata(messageMetadata);
  const actions = useContext(ThreadActionsContext);
  const [rating, setRating] = useState<WidgetFeedbackRating | null>(null);

  if (isRunning) return null;

  return (
    <div className={RATING_CONTROLS_CLASS} aria-label="Rate response">
      <TooltipIconButton
        tooltip="Good response"
        variant="ghost"
        size="icon"
        aria-label="Good response"
        aria-pressed={rating === "helpful"}
        data-testid="aichat_window_thumbs_up"
        data-submitted={rating === "helpful" ? "true" : undefined}
        style={{ borderRadius: "9999px" }}
        className={cn(
          RATING_BUTTON_CLASS,
          rating === "helpful" && "text-[rgba(0,24,35,0.56)]",
        )}
        onClick={() => {
          if (rating === "helpful") return;
          setRating("helpful");
          actions.onAssistantFeedback?.({ rating: "helpful", ...feedbackMetadata });
        }}
      >
        <span className={RATING_ICON_FRAME_CLASS} aria-hidden="true">
          {rating === "helpful" ? (
            <ThumbUpFilledIcon className={RATING_ICON_CLASS} />
          ) : (
            <ThumbUpOutlinedIcon className={RATING_ICON_CLASS} />
          )}
        </span>
      </TooltipIconButton>
      <TooltipIconButton
        tooltip="Bad response"
        variant="ghost"
        size="icon"
        aria-label="Bad response"
        aria-pressed={rating === "not_helpful"}
        data-testid="aichat_window_thumbs_down"
        data-submitted={rating === "not_helpful" ? "true" : undefined}
        style={{ borderRadius: "9999px" }}
        className={cn(
          RATING_BUTTON_CLASS,
          rating === "not_helpful" && "text-[rgba(0,24,35,0.56)]",
        )}
        onClick={() => {
          if (rating === "not_helpful") return;
          setRating("not_helpful");
          actions.onAssistantFeedback?.({ rating: "not_helpful", ...feedbackMetadata });
        }}
      >
        <span className={RATING_ICON_FRAME_CLASS} aria-hidden="true">
          {rating === "not_helpful" ? (
            <ThumbDownFilledIcon className={RATING_ICON_CLASS} />
          ) : (
            <ThumbDownOutlinedIcon className={RATING_ICON_CLASS} />
          )}
        </span>
      </TooltipIconButton>
    </div>
  );
};

function liveAssistanceMetadata(metadata: unknown): LiveAssistanceMessageMetadata | undefined {
  return (metadata as { custom?: { liveAssistance?: LiveAssistanceMessageMetadata } } | undefined)?.custom
    ?.liveAssistance;
}

function assistantFeedbackMetadata(metadata: unknown): Omit<AssistantFeedbackPayload, "rating"> {
  const custom = (metadata as {
    custom?: {
      clientMessageId?: unknown;
      traceId?: unknown;
      agentMetadata?: { langsmith?: { trace_id?: unknown; run_id?: unknown } };
    };
  } | undefined)?.custom;
  const langsmith = custom?.agentMetadata?.langsmith;
  return {
    clientMessageId: typeof custom?.clientMessageId === "string" ? custom.clientMessageId : undefined,
    traceId: typeof custom?.traceId === "string" ? custom.traceId : undefined,
    langsmithTraceId: typeof langsmith?.trace_id === "string" ? langsmith.trace_id : undefined,
    langsmithRunId: typeof langsmith?.run_id === "string" ? langsmith.run_id : undefined,
  };
}

function enrollmentConsultationMetadata(metadata: unknown): EnrollmentConsultationMetadata | undefined {
  const value = (metadata as { custom?: { enrollmentConsultation?: EnrollmentConsultationMetadata } } | undefined)
    ?.custom?.enrollmentConsultation;
  return value?.kind === "advisor_consultation" ? value : undefined;
}

function safetyExitMetadata(metadata: unknown): { kind?: string } | undefined {
  return (metadata as { custom?: { safetyExit?: { kind?: string } } } | undefined)?.custom?.safetyExit;
}

function isFollowUpSuggestion(value: unknown): value is FollowUpSuggestion {
  if (typeof value !== "object" || value === null) return false;
  const suggestion = value as Partial<FollowUpSuggestion>;
  return typeof suggestion.id === "string" &&
    typeof suggestion.label === "string" &&
    typeof suggestion.prompt === "string" &&
    typeof suggestion.category === "string" &&
    suggestion.source === "ai";
}

const USER_MESSAGE_INPUT_BLOCK_CLASS =
  "aui-user-message-content-wrapper relative col-start-2 flex max-w-[480px] flex-col items-end gap-[8px] self-end";
const USER_MESSAGE_CONTENT_BUBBLE_CLASS =
  "aui-user-message-content wrap-break-word rounded-md px-[16px] py-[12px] text-right text-[#001823]";
const USER_MESSAGE_CONTENT_BUBBLE_STYLE: CSSProperties = {
  ...CHAT_MESSAGE_BODY_TEXT_STYLE,
  backgroundColor: "var(--background-secondary-1, #F0F9FA)",
};

const UserMessage: FC = () => {
  const hasVisibleContent = useMessage((message) => hasVisibleMessageContent(message.content));

  return (
    <>
      <MessageBoundaryMarkers />
      <MessagePrimitive.Root
        className={USER_MESSAGE_ROOT_CLASS}
        data-role="user"
        data-testid="aichat_window_message-user"
      >
        <UserMessageAttachments />

        {hasVisibleContent && (
          <div className={USER_MESSAGE_INPUT_BLOCK_CLASS}>
            <UserMessageLabel />
            <div
              className={USER_MESSAGE_CONTENT_BUBBLE_CLASS}
              style={USER_MESSAGE_CONTENT_BUBBLE_STYLE}
            >
              <MessagePrimitive.Parts />
            </div>
          </div>
        )}
      </MessagePrimitive.Root>
    </>
  );
};

const UserMessageLabel: FC = () => {
  const timestamp = useMessageTime();
  return (
    <div
      className="flex items-center justify-end gap-1 text-right text-muted-foreground"
      style={USER_MESSAGE_LABEL_STYLE}
    >
      <span>{USER_LABEL}</span>
      <span aria-hidden="true" className="inline-block" style={USER_MESSAGE_DOT_SEPARATOR_STYLE} />
      <time>{timestamp}</time>
    </div>
  );
};

const LiveStudentMessageReceipt: FC = () => {
  const clientMessageId = useMessage((message) => {
    const metadata = message.metadata as
      | { custom?: { clientMessageId?: unknown; liveAssistance?: LiveAssistanceMessageMetadata } }
      | undefined;
    const custom = metadata?.custom;
    const liveAssistance = custom?.liveAssistance;
    const value = liveAssistance?.clientMessageId ?? custom?.clientMessageId;
    return typeof value === "string" ? value : null;
  });
  const deliveryStatus = useSocketStatusStore((state) =>
    getLiveStudentDeliveryStatus(state.liveAssistance, clientMessageId),
  );
  const deliveryMessage = useSocketStatusStore((state) =>
    getLiveStudentDeliveryMessage(state.liveAssistance, clientMessageId),
  );

  if (!deliveryStatus || !deliveryMessage) return null;

  return (
    <div
      className={cn(
        "aui-user-message-receipt mt-1 flex items-center justify-end gap-1 text-[11px] font-medium leading-none",
        deliveryStatus === "failed" ? "text-red-600" : "text-sky-700",
      )}
      aria-label={`Message ${deliveryMessage.toLowerCase()}`}
    >
      <LiveReceiptIcon status={deliveryStatus} />
      <span>{deliveryMessage}</span>
    </div>
  );
};

const LiveReceiptIcon: FC<{ status: LiveDeliveryStatus }> = ({ status }) => {
  if (status === "failed") return <AlertCircleIcon className="size-3.5" aria-hidden="true" />;
  if (status === "read" || status === "delivered") {
    return <CheckCheckIcon className="size-3.5" aria-hidden="true" />;
  }
  return <CheckIcon className="size-3.5" aria-hidden="true" />;
};

// UserActionBar (message edit trigger) intentionally removed — message editing is disabled.
// EditComposer below is kept for future re-enablement without full re-implementation.

const EditComposer: FC = () => (
  <MessagePrimitive.Root className="aui-edit-composer-wrapper mx-auto flex w-full max-w-(--thread-max-width) flex-col px-2 py-3">
    <ComposerPrimitive.Root className="aui-edit-composer-root ml-auto flex w-full max-w-[85%] flex-col rounded-2xl bg-muted">
      <ComposerPrimitive.Input
        className="aui-edit-composer-input min-h-14 w-full resize-none bg-transparent p-4 text-foreground text-sm outline-none"
        aria-label="Edit message"
        autoFocus
      />
      <div className="aui-edit-composer-footer mx-3 mb-3 flex items-center gap-2 self-end">
        <ComposerPrimitive.Cancel asChild>
          <Button variant="ghost" size="sm">Cancel</Button>
        </ComposerPrimitive.Cancel>
        <ComposerPrimitive.Send asChild>
          <Button size="sm">Update</Button>
        </ComposerPrimitive.Send>
      </div>
    </ComposerPrimitive.Root>
  </MessagePrimitive.Root>
);

const BranchPicker: FC<BranchPickerPrimitive.Root.Props> = ({ className, ...rest }) => (
  <BranchPickerPrimitive.Root
    hideWhenSingleBranch
    className={cn(
      "aui-branch-picker-root mr-2 -ml-2 inline-flex items-center text-muted-foreground text-xs",
      className,
    )}
    {...rest}
  >
    <BranchPickerPrimitive.Previous asChild>
      <TooltipIconButton tooltip="Previous response">
        <ChevronLeftIcon />
      </TooltipIconButton>
    </BranchPickerPrimitive.Previous>
    <span className="aui-branch-picker-state font-medium">
      <BranchPickerPrimitive.Number /> / <BranchPickerPrimitive.Count />
    </span>
    <BranchPickerPrimitive.Next asChild>
      <TooltipIconButton tooltip="Next response">
        <ChevronRightIcon />
      </TooltipIconButton>
    </BranchPickerPrimitive.Next>
  </BranchPickerPrimitive.Root>
);
